<template>
  <div class="o-container -has-toolbar">
    <Loader :loading="isLoading"></Loader>
    <!-- Delete modal -->
    <div class="c-takeover-modal" :class="{ 'is-visible' : showQuoteDelete }" v-if="dataLoaded">
      <table>
        <tr>
          <td>
            <p>Are you sure you want to delete this estimate?</p>
          </td>
        </tr>
        <tr>
          <td>
            <a class="c-button c-button--primary" @click.prevent="deleteQuote">Delete this estimate</a>
          </td>
        </tr>
        <tr>
          <td>
            <a v-on:click="showQuoteDelete = 0">Cancel</a>
          </td>
        </tr>
      </table>
    </div>
    <!-- Quote conversion modal -->
    <div class="c-takeover-modal" :class="{ 'is-visible' : showQuoteConvert }" v-if="dataLoaded">
      <table>
        <tr>
          <!-- If currently an estimate and has no custom products (if sub-admin) -->
          <td v-if="(userLevel === 'admin' && workingData.quoteType === 'estimate') || (userLevel === 'sub-admin' && workingData.quoteType === 'estimate' && !hasCustomProducts)">
            <p>Are you sure you want to convert this estimate to a quote? Please review all values, specifically that of discounts.</p>
          </td>
          <!-- If currently a quote and has no custom products (if sub-admin) -->
          <td v-if="(userLevel === 'admin' && workingData.quoteType === 'quote') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && !hasCustomProducts)">
            <p>Are you sure you want to convert this quote to an estimate? Please review all values, specifically that of discounts.</p>
          </td>
          <!-- If currently a quote and has custom products and user is sub-admin -->
          <td v-if="(userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts)">
            <p>Are you sure you want to convert this quote to an estimate? Since this quote currently contains custom products, it will once again have to pass through Memo approval to return to quote status. Please review all values, specifically that of discounts.</p>
          </td>
          <!-- If currently an estimate and has custom products (if sub-admin) -->
          <td v-if="(userLevel === 'sub-admin' && workingData.quoteType === 'estimate' && hasCustomProducts)">
            <p>Your estimate contains a custom product(s). To convert to a quote a Memo staff member must first approve the estimate. Please review all values, specifically that of discounts.</p>
          </td>
        </tr>
        <tr>
          <td>
            <!-- If currently an estimate and has no custom products (if sub-admin) -->
            <a class="c-button c-button--primary" v-if="(userLevel === 'admin' && workingData.quoteType === 'estimate') || (userLevel === 'sub-admin' && workingData.quoteType === 'estimate' && !hasCustomProducts)" @click.prevent="convertQuote(workingData.quoteType)">Convert to quote</a>
            <!-- If currently a quote and has no custom products (if sub-admin) -->
            <a class="c-button c-button--primary" v-if="(userLevel === 'admin' && workingData.quoteType === 'quote') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote')" @click.prevent="convertQuote(workingData.quoteType)">Convert to estimate</a>
            <!-- If currently an estimate and has custom products (if sub-admin) -->
            <a class="c-button c-button--primary" v-if="(userLevel === 'sub-admin' && workingData.quoteType === 'estimate' && hasCustomProducts)" @click.prevent="convertQuote(workingData.quoteType)">Request Quote Approval</a>
          </td>
        </tr>
        <tr>
          <td>
            <a v-on:click="showQuoteConvert = 0">Cancel</a>
          </td>
        </tr>
      </table>
    </div>
    <div class="o-layout" v-if="dataLoaded">
      <div class="o-layout__primary">
          <table class="c-table" v-if="workingData.products.length > 0">
            <thead>
              <tr>
                <th class="c-table__heading">Line</th>
                <th class="c-table__heading" colspan="2">QTY.</th>
                <th class="c-table__heading">Options, Finishes, &amp; Textiles</th>
                <th class="c-table__heading"></th>
                <th class="c-table__heading c-table__heading--align-right">Costs/Unit</th>
                <th class="c-table__heading c-table__heading--align-right">Extended Costs</th>
                <th class="c-table__heading"></th>
              </tr>
            </thead>
            <tbody v-for="lineItem, key in workingData.products" :id="String('product__'+ (key + 1))">
              <tr class="c-table__row">
                <td class="c-table__spacer" colspan="8">&nbsp;</td>
              </tr>
              <tr class="c-table__row" v-if="(workingData.quoteType == 'estimate' && lineItem.product.productSlug == 'custom-product' && userLevel == 'basic' && workingData.approvalState !== 'adminApprovalRequested')">
                <td class="c-table__spacer">&nbsp;</td>
                <td class="c-table__spacer c-permissions-msg" colspan="7">To edit a custom product, please contact us at <a href="mailto:quotes@memofurniture.com">quotes@memofurniture.com</a></td>
              </tr>
              <tr class="c-table__row">
                <td class="c-table__cell">{{ String(key + 1).padStart(3,0) }}</td>
                <td class="c-table__cell" colspan="7">
                  <h2 v-if="lineItem.product.productName" class="c-table__subheading"><span v-if="lineItem.product.productCode && lineItem.product.productSlug == 'custom-product'">Custom Product </span>{{ lineItem.product.productName }} <small v-if="lineItem.product.productCode">{{ lineItem.product.productCode }}</small></h2>
                </td>
              </tr>
              <tr class="c-table__row">
                <td class="c-table__cell">&nbsp;</td>
                <td class="c-table__cell" style="min-width: 100px;">
                  <input type="number" min="1" class="c-input c-input--char-4" pattern="[0-9]*" v-model.number="lineItem.product.quantity" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (lineItem.product.productSlug == 'custom-product' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
                </td>
                <td class="c-table__cell" style="min-width: 100px;">
                  <span v-if="lineItem.product.productSlug != 'custom-product'"><img :src="fetchLineArtUrl(workingData.products[key].product)" alt="Product line art" style="margin: -10px; max-width: 100px;"></span>
                </td>
                <td class="c-table__cell">
                  <p v-for="option in workingData.products[key].product.species">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.moduleOne">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.moduleOne[0].code !== 'SET'" v-for="option in workingData.products[key].product.moduleTwo">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.moduleOne[0].code !== 'SET'" v-for="option in workingData.products[key].product.moduleThree">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.moduleThree[0].code !== 'SET'" v-for="option in workingData.products[key].product.moduleFour">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.moduleThree[0].code !== 'SET'" v-for="option in workingData.products[key].product.moduleFive">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.armHeight">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.tableTopSize">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.armCapMaterial">{{ option.code + ' - ' + option.name }}<span v-if="workingData.products[key].product.armCapMaterialName">, <em>{{ workingData.products[key].product.armCapMaterialName }}</em></span></p>
                  <p v-for="option in workingData.products[key].product.pads" v-if="workingData.products[key].product.pads.length && (workingData.products[key].product.pads[0].code !== 'XX')">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.plinthType">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.plinthMaterials">{{ option.code + ' - ' + option.name }}<span v-if="workingData.products[key].product.plinthMaterialName && option.code === 'LAM'">, <em>{{ workingData.products[key].product.plinthMaterialName }}</em></span></p>
                  <p v-for="option in workingData.products[key].product.plinthLaminateBrands" v-if="workingData.products[key].product.plinthMaterials.length && workingData.products[key].product.plinthMaterials[0].code === 'LAM'">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.plinthFinishes">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.tables" v-if="workingData.products[key].product.tables.length && (workingData.products[key].product.tables[0].code !== 'XX')">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.partitions" v-if="workingData.products[key].product.partitions.length && (workingData.products[key].product.partitions[0].code !== 'XX')">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="(((workingData.products[key].product.moduleOne.length === 1 && workingData.products[key].product.moduleOne[0].code === 'INL') || (workingData.products[key].product.moduleThree.length === 1 && workingData.products[key].product.moduleThree[0].code === 'INL') || (workingData.products[key].product.moduleFive.length === 1 && workingData.products[key].product.moduleFive[0].code === 'INL') ) || ( !(workingData.products[key].product.moduleOne.length) || !(workingData.products[key].product.moduleThree.length) || !(workingData.products[key].product.moduleFive.length)) && !isMaru(workingData.products[key].product.baseCode)) || (isMaru(workingData.products[key].product.baseCode) && ((workingData.products[key].product.tableTopMaterial.length && !workingData.products[key].product.tables.length) || (workingData.products[key].product.tableTopMaterial.length && workingData.products[key].product.tables.length && workingData.products[key].product.tables[0].code !== 'XX')))"><span v-for="option in workingData.products[key].product.tableTopMaterial">{{ option.code + ' - ' + option.name }}</span><span v-if="workingData.products[key].product.tableTopMaterialName && workingData.products[key].product.tableTopMaterial !== 'FTT'">, <em>{{ workingData.products[key].product.tableTopMaterialName }}</em></span></p>
                  <p v-for="option in workingData.products[key].product.tableTopMaterialFinishSideA">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.tableTopMaterialFinishSideB">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.tableTopMaterialForboFinish" v-if="workingData.products[key].product.tableTopMaterial.length && workingData.products[key].product.tableTopMaterial[0].code === 'FTT'">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.tableTopMaterialMetalFinish" v-if="workingData.products[key].product.tableTopMaterial.length && workingData.products[key].product.tableTopMaterial[0].code === 'MTT'">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.largeInsertTopMaterial">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.largeInsertTopMaterialForboFinish" v-if="workingData.products[key].product.largeInsertTopMaterial.length && workingData.products[key].product.largeInsertTopMaterial[0].code === 'FTL'">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.largeInsertTopMaterialSolidSurfaceFinish" v-if="workingData.products[key].product.largeInsertTopMaterial.length && workingData.products[key].product.largeInsertTopMaterial[0].code === 'SSL'">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.smallInsertTopMaterial">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.smallInsertTopMaterialForboFinish" v-if="workingData.products[key].product.smallInsertTopMaterial.length && workingData.products[key].product.smallInsertTopMaterial[0].code === 'FTS'">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.smallInsertTopMaterialSolidSurfaceFinish" v-if="workingData.products[key].product.smallInsertTopMaterial.length && workingData.products[key].product.smallInsertTopMaterial[0].code === 'SSS'">{{ option.code + ' - ' + option.name }}</p>

                  <p v-if="workingData.products[key].product.baseCode == 'NC01-T1' && workingData.products[key].product.smallInsertTopMaterial.length && (workingData.products[key].product.smallInsertTopMaterial[0].code === 'VAS' || workingData.products[key].product.smallInsertTopMaterial[0].code === 'VWS' || workingData.products[key].product.smallInsertTopMaterial[0].code === 'SAS' || workingData.products[key].product.smallInsertTopMaterial[0].code === 'SWS')" v-for="option in workingData.products[key].product.woodFinish">{{ option.code + ' - ' + option.name }}</p>

                  <p v-if="workingData.products[key].product.stoneQuartzTableTop.length && workingData.products[key].product.smallInsertTopMaterial.length && workingData.products[key].product.smallInsertTopMaterial[0].code == 'STS' && workingData.products[key].product.baseCode === 'NC01-T1'" v-for="option in workingData.products[key].product.stoneQuartzTableTop">{{ option.code + ' - ' + option.name }}</p>
                  
                  <p v-for="option in workingData.products[key].product.caseworkSurfaceMaterial">{{ option.code + ' - ' + option.name }}<span v-if="workingData.products[key].product.caseworkSurfaceMaterialName">, <em>{{ workingData.products[key].product.caseworkSurfaceMaterialName }}</em></span></p>
                  
                  <p v-for="option in workingData.products[key].product.columnShroudPaint">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.legMaterial">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.legMaterial[0] && workingData.products[key].product.legMaterial[0].code !== 'LW'" v-for="option in workingData.products[key].product.metalFinish">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="((workingData.products[key].product.legMaterial[0] && workingData.products[key].product.legMaterial[0].code !== 'LM') || !workingData.products[key].product.legMaterial[0]) && !(workingData.products[key].product.baseCode == 'MM01-T1' || workingData.products[key].product.baseCode == 'MM01-T2' || workingData.products[key].product.baseCode == 'MM01-T3') && !((workingData.products[key].product.baseCode == 'HK01-T1' || workingData.products[key].product.baseCode == 'HK01-T2') && workingData.products[key].product.tableTopMaterial[0].code == 'FTT') && (workingData.products[key].product.finishType !== 'paintedWood')" v-for="option in workingData.products[key].product.woodFinish">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="(workingData.products[key].product.paintedWood.length) && (workingData.products[key].product.finishType !== 'woodFinish')" v-for="option in workingData.products[key].product.paintedWood">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="(workingData.products[key].product.largeInsertTopMaterial.length && (workingData.products[key].product.largeInsertTopMaterial[0].code == 'VAL' || workingData.products[key].product.largeInsertTopMaterial[0].code == 'VWL')) || (workingData.products[key].product.smallInsertTopMaterial.length && (workingData.products[key].product.smallInsertTopMaterial[0].code == 'VAL' || workingData.products[key].product.smallInsertTopMaterial[0].code == 'VWL'))" v-for="option in workingData.products[key].product.woodFinish">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.stoneQuartzTableTop.length && workingData.products[key].product.smallInsertTopMaterial.length && workingData.products[key].product.smallInsertTopMaterial[0].code == 'STS' && workingData.products[key].product.baseCode !== 'NC01-T1'" v-for="option in workingData.products[key].product.stoneQuartzTableTop">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.metalFinish[0] && (workingData.products[key].product.baseCode == 'MM01-T1' || workingData.products[key].product.baseCode == 'MM01-T2' || workingData.products[key].product.baseCode == 'MM01-T3' || workingData.products[key].product.baseCode == 'HK01-01' || workingData.products[key].product.baseCode == 'HK01-09' || workingData.products[key].product.baseCode == 'HK01-11' || isBSide(workingData.products[key].product.baseCode))" v-for="option in workingData.products[key].product.metalFinish">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.swivelBaseType">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.leatherArmWrap" v-if="option.code !== 'LXXX'">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.moistureBarrier">MB - Moisture Barrier</p>
                  <p v-if="workingData.products[key].product.removeableCovers">RC - Removeable Covers</p>
                  <p v-if="workingData.products[key].product.calTb133">FB - CAL TB133</p>
                  <p v-if="workingData.products[key].product.casters">CAS - Casters</p>
                  <p v-if="workingData.products[key].product.removeableArmPads">AP - Removeable Arm Pads (Pair)</p>
                  <p v-if="workingData.products[key].product.hangingFixture">DJ - Hanging Fixture</p>
                  <p v-for="option in workingData.products[key].product.hingesOne">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.hingesTwo">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.hingesThree">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.pull">{{ option.code + ' - ' + option.name }}</p>
                  <p v-for="option in workingData.products[key].product.wrap">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.drawerLiner">DL - Drawer Liner</p>
                  <p v-for="option in workingData.products[key].product.veneerOuterCase">{{ option.code + ' - ' + option.name }}</p>
                  <p v-if="workingData.products[key].product.paperTowelDispenser">TD - Paper Towel Dispenser Cut-Out</p>
                  <p v-if="workingData.products[key].product.extraAdjustableShelf">AS - Extra Adjustable Shelf</p>
                  <p v-if="workingData.products[key].product.fileHolders">FH - File Holders</p>
                  <p v-if="workingData.products[key].product.gloveCutOuts">GH - Glove Cut Outs</p>
                  <p v-for="option in workingData.products[key].product.powerModules" v-if="workingData.products[key].product.powerModules.length && (workingData.products[key].product.powerModules[0].code !== 'XX')">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.singleTextileGrade" v-if="((workingData.products[key].product.yardageSeat > 0 || workingData.products[key].product.yardageBack > 0 || workingData.products[key].product.yardageArm > 0 || workingData.products[key].product.yardageTAN > 0 || workingData.products[key].product.yardageSET > 0) && (workingData.products[key].product.multTextilesOption.length === 0 || (workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'XX'))) || ((isMaru(workingData.products[key].product.baseCode) || isApril(workingData.products[key].product.baseCode)) && ((workingData.products[key].product.pads.length && workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'XX') || (workingData.products[key].product.pads.length && !workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.pads[0].code !== 'XX'))) || (workingData.products[key].product.baseCode == 'KN01-S1' && workingData.products[key].product.tableTopMaterial[0].code == 'UPH') || (workingData.products[key].product.yardageTotal > 0 && (workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'XX') && (workingData.products[key].product.baseCode == 'AB01-V' || workingData.products[key].product.baseCode == 'AB01-H'))">Textile - {{ option.name }}<span v-if="workingData.products[key].product.singleTextileGradeName">, <em>{{ workingData.products[key].product.singleTextileGradeName }}</em></span><span v-if="buildModuleYardage(workingData.products[key].product) > 0 || parseFloat(workingData.products[key].product.yardageTotal) > 0 || (workingData.products[key].product.armHeight.length) || workingData.products[key].product.pads.length">, <em>{{ buildModuleYardage(workingData.products[key].product) ? buildModuleYardage(workingData.products[key].product) : (workingData.products[key].product.armHeight.length ? (parseFloat(workingData.products[key].product.armHeight[0].armYardage) + parseFloat(workingData.products[key].product.yardageSeat) + parseFloat(workingData.products[key].product.yardageBack)).toFixed(2) : (workingData.products[key].product.pads.length ? (workingData.products[key].product.pads[0].code !== 'XX' ? (parseFloat(workingData.products[key].product.pads[0].padYardage) + parseFloat(workingData.products[key].product.yardageSeat) + parseFloat(workingData.products[key].product.yardageBack)).toFixed(2) : parseFloat(workingData.products[key].product.yardageTotal)) : parseFloat(workingData.products[key].product.yardageTotal))) }} yards per unit</em></span></p>

                  <p v-for="option in workingData.products[key].product.multTextilesOption" v-if="option.code !== 'XX'">{{ option.code + ' - ' + option.name }}</p>

                  <p v-for="option in workingData.products[key].product.armTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'AT'">Arm Textile - {{ option.name }}<span v-if="workingData.products[key].product.armTextileGradeName">, <em>{{ workingData.products[key].product.armTextileGradeName }}</em></span><span v-if="parseFloat(workingData.products[key].product.yardageArm) > 0 || (workingData.products[key].product.armHeight.length)">, <em>{{ (workingData.products[key].product.armHeight.length ? parseFloat(workingData.products[key].product.armHeight[0].armYardage).toFixed(2) : parseFloat(workingData.products[key].product.yardageArm)) }} yards per unit</em></span></p>

                  <p v-for="option in workingData.products[key].product.armBackTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'ST'">Arm, Back Textile - {{ option.name }}<span v-if="workingData.products[key].product.armBackTextileGradeName">, <em>{{ workingData.products[key].product.armBackTextileGradeName }}</em></span><span v-if="(parseFloat(workingData.products[key].product.yardageArm) + parseFloat(workingData.products[key].product.yardageBack)) > 0 || (workingData.products[key].product.armHeight.length)">, <em>{{ (workingData.products[key].product.armHeight.length ? (parseFloat(workingData.products[key].product.armHeight[0].armYardage) + parseFloat(workingData.products[key].product.yardageBack)).toFixed(2) : parseFloat(workingData.products[key].product.yardageArm) + parseFloat(workingData.products[key].product.yardageBack)) }} yards per unit</em></span></p>
                  <p v-for="option in workingData.products[key].product.armSeatTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'BT'">Arm, Seat Textile - {{ option.name }}<span v-if="workingData.products[key].product.armSeatTextileGradeName">, <em>{{ workingData.products[key].product.armSeatTextileGradeName }}</em></span><span v-if="(parseFloat(workingData.products[key].product.yardageArm) + parseFloat(workingData.products[key].product.yardageSeat)) > 0 || (workingData.products[key].product.armHeight.length)">, <em>{{ (workingData.products[key].product.armHeight.length ? (parseFloat(workingData.products[key].product.armHeight[0].armYardage) + parseFloat(workingData.products[key].product.yardageSeat)).toFixed(2) : parseFloat(workingData.products[key].product.yardageArm) + parseFloat(workingData.products[key].product.yardageSeat)) }} yards per unit</em></span></p>
                  <p v-for="option in workingData.products[key].product.backTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && (workingData.products[key].product.multTextilesOption[0].code === 'TA' || workingData.products[key].product.multTextilesOption[0].code === 'BT')">Back Textile - {{ option.name }}<span v-if="workingData.products[key].product.backTextileGradeName">, <em>{{ workingData.products[key].product.backTextileGradeName }}</em></span><span v-if="parseFloat(workingData.products[key].product.yardageBack) > 0">, <em>{{ parseFloat(workingData.products[key].product.yardageBack) }} yards per unit</em></span></p>

                  <p v-for="option in workingData.products[key].product.seatTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && (workingData.products[key].product.multTextilesOption[0].code === 'TA' || workingData.products[key].product.multTextilesOption[0].code === 'ST')">Seat Textile - {{ option.name }}<span v-if="workingData.products[key].product.seatTextileGradeName">, <em>{{ workingData.products[key].product.seatTextileGradeName }}</em></span><span v-if="(workingData.products[key].product.pads.length && parseFloat(workingData.products[key].product.pads[0].padYardage) > 0) || (parseFloat(workingData.products[key].product.yardageSeat) > 0)">, <em>{{ (workingData.products[key].product.pads.length ? (workingData.products[key].product.pads[0].code !== 'XX' ? (parseFloat(workingData.products[key].product.pads[0].padYardage)).toFixed(2) : workingData.products[key].product.yardage.seat) : workingData.products[key].product.yardageSeat) }} yards per unit</em></span></p>

                  <p v-for="option in workingData.products[key].product.seatBackTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'AT'">Seat, Back Textile - {{ option.name }}<span v-if="workingData.products[key].product.seatBackTextileGradeName">, <em>{{ workingData.products[key].product.seatBackTextileGradeName }}</em></span><span v-if="(parseFloat(workingData.products[key].product.yardageSeat) + parseFloat(workingData.products[key].product.yardageBack)) > 0">, <em>{{ parseFloat(workingData.products[key].product.yardageSeat) + parseFloat(workingData.products[key].product.yardageBack) }} yards per unit</em></span></p>

                  <p v-for="option in workingData.products[key].product.sideATextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'AB'">Side A Textile - {{ option.name }}<span v-if="workingData.products[key].product.sideATextileGradeName">, <em>{{ workingData.products[key].product.sideATextileGradeName }}</em></span><span v-if="parseFloat(workingData.products[key].product.yardageSideA) > 0">, <em>{{ parseFloat(workingData.products[key].product.yardageSideA) }} yards per unit</em></span></p>

                  <p v-for="option in workingData.products[key].product.sideBTextileGrade" v-if="workingData.products[key].product.multTextilesOption.length && workingData.products[key].product.multTextilesOption[0].code === 'AB'">Side B Textile - {{ option.name }}<span v-if="workingData.products[key].product.sideBTextileGradeName">, <em>{{ workingData.products[key].product.sideBTextileGradeName }}</em></span><span v-if="parseFloat(workingData.products[key].product.yardageSideB) > 0">, <em>{{ parseFloat(workingData.products[key].product.yardageSideB) }} yards per unit</em></span></p>
                  
                  <p v-if="workingData.products[key].product.oversizeGoods">Oversize Goods</p>
                </td>
                <td class="c-table__cell c-table__cell--align-right">
                  <p><strong>List</strong></p>
                  <!-- <p v-if="lineItem.product.unitTextilePrice > 0"><strong>Textiles</strong></p> -->
                  <p><strong>Disc.</strong></p>
                  <p><strong>Net</strong></p>
                  <p v-if="workingData.salesTax > 0"><strong>Tax</strong></p>
                </td>
                <td class="c-table__cell c-table__cell--align-right">
                  <p>{{ lineItem.product.unitListPrice | formatPrice }}</p>
                  <!-- <p v-if="lineItem.product.unitTextilePrice > 0">{{ lineItem.product.unitTextilePrice | formatPrice }}</p> -->
                  <p>
                    <div class="c-overview-line-discount">
                      <input type="number" class="c-input--qo-disc" v-model.number="lineItem.product.productDiscount" :min="0" :max="100" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (lineItem.product.productSlug == 'custom-product' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
                    </div>
                  </p>
                  <p>{{ lineItem.product.unitNetPrice = unitNetRowPrice[key] | formatNetPrice }}</p>
                  <p>{{ lineItem.product.unitTaxPrice = unitTaxRowPrice[key] | formatNetPrice }}</p>
                </td>
                <td class="c-table__cell c-table__cell--align-right">
                  <p>{{ lineItem.product.extendedListPrice = extendedListRowPrice[key] | formatPrice }}</p>
                  <!-- <p v-if="lineItem.product.unitTextilePrice > 0">{{ lineItem.product.extendedTextilePrice = extendedTextileRowPrice[key] | formatPrice }}</p> -->
                  <p>&nbsp;</p>
                  <p>{{ lineItem.product.extendedNetPrice = extendedNetRowPrice[key] | formatNetPrice }}</p>
                  <p>{{ lineItem.product.extendedTaxPrice = extendedTaxRowPrice[key] | formatNetPrice }}</p>
                </td>
                <td class="c-table__cell c-table__cell--align-right">
                  <a class="c-edit" :href="'/quote/edit/'+quoteId+'/product/'+key+'/'+lineItem.product.productID" v-if="!(workingData.quoteType == 'quote' && userLevel == 'basic') && !(workingData.quoteType == 'estimate' && lineItem.product.productSlug == 'custom-product' && userLevel == 'basic') && !(userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') && !(userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) && !isInvalid">
                    Edit
                    <svg width="15" height="16" viewBox="0 0 15 16" xmlns="http://www.w3.org/2000/svg"><path d="M14.846 1.667a.548.548 0 0 1 0 .762l-.388.388-1.53-1.538.387-.381a.535.535 0 0 1 .762 0l.77.769zm-8.1 8.1l6.95-6.95.381.38-7.075 7.084-1.912.38.38-1.911 7.076-7.083.381.38-6.944 6.96.059.702.703.059zm5.31-3.127c.146 0 .263.125.263.271v8.306a.533.533 0 0 1-.535.535H.534a.538.538 0 0 1-.535-.535V3.967c0-.3.242-.535.535-.535H8.84c.146 0 .264.117.264.264a.269.269 0 0 1-.264.27H.534v11.251h11.25V6.911c0-.146.125-.27.271-.27z" fill="#00586F" fill-rule="evenodd"/></svg>
                  </a>
                  <a class="c-edit -is-disabled" v-if="(workingData.quoteType == 'quote' && userLevel == 'basic') || (workingData.quoteType == 'estimate' && lineItem.product.productSlug == 'custom-product' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid">
                    Edit
                    <svg width="15" height="16" viewBox="0 0 15 16" xmlns="http://www.w3.org/2000/svg"><path d="M14.846 1.667a.548.548 0 0 1 0 .762l-.388.388-1.53-1.538.387-.381a.535.535 0 0 1 .762 0l.77.769zm-8.1 8.1l6.95-6.95.381.38-7.075 7.084-1.912.38.38-1.911 7.076-7.083.381.38-6.944 6.96.059.702.703.059zm5.31-3.127c.146 0 .263.125.263.271v8.306a.533.533 0 0 1-.535.535H.534a.538.538 0 0 1-.535-.535V3.967c0-.3.242-.535.535-.535H8.84c.146 0 .264.117.264.264a.269.269 0 0 1-.264.27H.534v11.251h11.25V6.911c0-.146.125-.27.271-.27z" fill="#00586F" fill-rule="evenodd"/></svg>
                  </a>


                  <br/>
                  <a class="c-delete" @click="deleteProduct(key)" v-if="!(workingData.quoteType == 'quote' && userLevel == 'basic') && !(workingData.quoteType == 'estimate' && lineItem.product.productSlug == 'custom-product' && userLevel == 'basic') && !(userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') && !(userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) && !isInvalid">
                    Delete
                    <svg width="13" height="16" xmlns="http://www.w3.org/2000/svg"><path d="M12.21 1.71c.165 0 .29.134.29.29a.282.282 0 0 1-.29.281h-.28L10.79 14.86c0 .625-.517 1.141-1.15 1.141H3.36a1.149 1.149 0 0 1-1.15-1.14L1.07 2.28H.79A.282.282 0 0 1 .5 2c0-.156.125-.29.29-.29h3.14v-.57C3.93.509 4.437 0 5.07 0h2.86c.633 0 1.14.508 1.14 1.14v.57h3.14zM4.5 1.14v.57h4v-.57a.574.574 0 0 0-.57-.57H5.07a.574.574 0 0 0-.57.57zm5.71 13.72L11.36 2.28H1.64L2.79 14.86c0 .313.25.57.57.57h6.28c.32 0 .57-.257.57-.57zM6.5 14.28A.287.287 0 0 1 6.21 14V3.71c0-.155.134-.28.29-.28.156 0 .29.125.29.28V14a.287.287 0 0 1-.29.281zm-1.984.008a.287.287 0 0 1-.297-.273l-.57-10.29a.28.28 0 0 1 .265-.296.288.288 0 0 1 .305.265l.57 10.29a.3.3 0 0 1-.273.304zm3.968 0a.3.3 0 0 1-.273-.305l.57-10.273a.288.288 0 0 1 .305-.266.288.288 0 0 1 .266.305l-.57 10.266a.287.287 0 0 1-.298.273z" fill="#00586F" fill-rule="evenodd"/></svg>
                  </a>
                  <a class="c-delete -is-disabled" v-if="(workingData.quoteType == 'quote' && userLevel == 'basic') || (workingData.quoteType == 'estimate' && lineItem.product.productSlug == 'custom-product' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid">
                    Delete
                    <svg width="13" height="16" xmlns="http://www.w3.org/2000/svg"><path d="M12.21 1.71c.165 0 .29.134.29.29a.282.282 0 0 1-.29.281h-.28L10.79 14.86c0 .625-.517 1.141-1.15 1.141H3.36a1.149 1.149 0 0 1-1.15-1.14L1.07 2.28H.79A.282.282 0 0 1 .5 2c0-.156.125-.29.29-.29h3.14v-.57C3.93.509 4.437 0 5.07 0h2.86c.633 0 1.14.508 1.14 1.14v.57h3.14zM4.5 1.14v.57h4v-.57a.574.574 0 0 0-.57-.57H5.07a.574.574 0 0 0-.57.57zm5.71 13.72L11.36 2.28H1.64L2.79 14.86c0 .313.25.57.57.57h6.28c.32 0 .57-.257.57-.57zM6.5 14.28A.287.287 0 0 1 6.21 14V3.71c0-.155.134-.28.29-.28.156 0 .29.125.29.28V14a.287.287 0 0 1-.29.281zm-1.984.008a.287.287 0 0 1-.297-.273l-.57-10.29a.28.28 0 0 1 .265-.296.288.288 0 0 1 .305.265l.57 10.29a.3.3 0 0 1-.273.304zm3.968 0a.3.3 0 0 1-.273-.305l.57-10.273a.288.288 0 0 1 .305-.266.288.288 0 0 1 .266.305l-.57 10.266a.287.287 0 0 1-.298.273z" fill="#00586F" fill-rule="evenodd"/></svg>
                  </a>
                </td>
              </tr>
              <tr class="c-table__row">
                <td class="c-table__cell">&nbsp;</td>
                <td class="c-table__cell c-table__cell--footnotes" colspan="7">
                  <p v-if="lineItem.product.productDescription || lineItem.product.optionsFinishesTextiles || lineItem.product.productTag || lineItem.product.additionalNotes">
                    <span class="additional -is-col" v-if="lineItem.product.productDescription"><strong>Description:</strong> <div class="c-table__product-description" v-html="lineItem.product.productDescription"></div></span>
                    <span class="additional -is-col -is-right-col" v-if="lineItem.product.productTag"><strong>Tags:</strong> {{ lineItem.product.productTag }}</span>
                    <span class="additional -is-col -is-right-col" v-if="lineItem.product.optionsFinishesTextiles"><strong>Options, Finishes, &amp; Textiles:</strong> {{ lineItem.product.optionsFinishesTextiles }}</span>
                    <span class="additional -is-col" v-if="lineItem.product.additionalNotes"><strong>Additional Notes:</strong> {{ lineItem.product.additionalNotes }}</span>
                  </p>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="c-table__row c-table__total-row">
                <td class="c-table__cell">&nbsp;</td>
                <td colspan="7" class="c-table__cell c-table__cell--align-right">
                  <div class="c-table__total">
                    <span><br/>Totals</span>
                    <div class="c-table__total-price">
                      <div class="c-table__total-price__row">
                        <div>List</div><div>{{ workingData.totalListPrice = quoteListPrice | formatPrice }}</div>
                      </div>
                      <div style="display: none;">{{ workingData.totalTextilePrice = quoteTextilePrice }}</div>
                      <!-- <div class="c-table__total-price__row" v-if="workingData.totalTextilePrice > 0">
                        <div>Textiles</div><div>{{ workingData.totalTextilePrice | formatPrice }}</div>
                      </div> -->
                      <div class="c-table__total-price__row">
                        <div>Net</div><div>{{ workingData.totalNetPrice = quoteNetPrice | formatNetPrice }}</div>
                      </div>
                      <div class="c-table__total-price__row" v-if="quoteFreightSurchargeTotalPrice > 0">
                        <div>Freight</div><div>{{ quoteFreightSurchargeTotalPrice | formatNetPrice }}</div>
                      </div>
                      <div style="display: none;">{{ minimumOrderSurchargeTotal = (totalUnits < workingData.minimumOrderThreshold && workingData.waiveMinimumOrderSurcharge !== 'yes') ? workingData.minimumOrderSurcharge : 0 }}</div>
                      <div class="c-table__total-price__row" v-if="minimumOrderSurchargeTotal > 0">
                        <div>Minimum Order Surcharge</div><div>{{ workingData.minimumOrderSurcharge | formatNetPrice }}</div>
                      </div>
                      <div style="display: none;">{{ workingData.totalTaxPrice = workingData.salesTax > 0 ? Number(parseFloat(((workingData.totalNetPrice + quoteFreightSurchargeTotalPrice + minimumOrderSurchargeTotal) * workingData.salesTax) / 100).toFixed(2).replace(/\.00/g,'')) : 0 }}</div>
                      <div class="c-table__total-price__row" v-if="workingData.salesTax > 0">
                        <div>Tax</div><div>{{ workingData.totalTaxPrice | formatNetPrice }}</div>
                      </div>
                      <div class="c-table__total-price__row">
                        <div>Total Due</div><div><strong>{{ workingData.totalQuotePrice = Number(parseFloat(quoteNetPrice + quoteFreightSurchargeTotalPrice + minimumOrderSurchargeTotal + workingData.totalTaxPrice).toFixed(2).replace(/\.00/g,'')) | formatNetPrice }}</strong></div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
          <div class="c-cta" v-if="dataLoaded && !(workingData.products.length > 0 && workingData.quoteType == 'quote' && userLevel == 'basic') && !(userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') && !(userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) && !(isInvalid && workingData.products.length > 0)" v-bind:class="[workingData.products.length > 0 ? '-has-top-margin' : '']">
            <p class="c-cta__description" v-if="workingData.products.length < 1">Looks like you don't have any products yet.</p>
            <a :href="'/quote/edit/'+this.quoteId+'/add'" class="c-button c-button--primary" v-if="workingData.products.length < 1 && !(workingData.quoteType == 'quote' && userLevel == 'basic') && !isInvalid">Add your first product</a>
            <p class="c-cta__description" v-if="workingData.products.length > 0 && !(workingData.quoteType == 'quote' && userLevel == 'basic') && !isInvalid">Need to add more products?</p>
            <a :href="'/quote/edit/'+this.quoteId+'/add'" class="c-button c-button--primary" v-if="workingData.products.length > 0 && !(workingData.quoteType == 'quote' && userLevel == 'basic') && !isInvalid">Add a product</a>
          </div>
      </div>

      <div class="o-layout__secondary">
        <div class="c-sidebar">
          <form action="">

            <div class="c-form-row">
              <label for="quoteType" class="c-label c-toggle" :class="[((userLevel === 'admin' || (userLevel === 'sub-admin' && (!hasCustomProducts && workingData.quoteType === 'estimate')) || (userLevel === 'sub-admin' && workingData.quoteType === 'quote')) && !isInvalid) ? '' : '-is-disabled']">Type
                <button class="c-label--info" v-if="(userLevel === 'sub-admin' && hasCustomProducts && workingData.quoteType === 'estimate') && !isInvalid" @click.prevent="showQuoteConvert = 1"></button>
                <input name="quoteType" id="quoteType" type="checkbox" v-model="workingData.quoteType" true-value="quote" false-value="estimate" :disabled="((userLevel === 'admin' || (userLevel === 'sub-admin' && (!hasCustomProducts && workingData.quoteType === 'estimate')) || (userLevel === 'sub-admin' && workingData.quoteType === 'quote')) && !isInvalid) ? false : true">
                <span class="c-toggle--toggler"></span>
                <span class="c-toggle--helper-text --left">Estimate</span>
                <span class="c-toggle--helper-text --right">Quote</span>
                <button class="c-button c-button--trans-takeover" @click.prevent="showQuoteConvert = 1" v-if="((userLevel === 'admin' || (userLevel === 'sub-admin' && (!hasCustomProducts && workingData.quoteType === 'estimate')) || (userLevel === 'sub-admin' && workingData.quoteType === 'quote')) && !isInvalid)"></button>
              </label>
              <div style="display: none;">{{ workingData.quoteType = workingData.quoteType ? workingData.quoteType : 'estimate' }}</div>
              <div style="display: none;">{{ savedData.quoteID = savedData.quoteID ? savedData.quoteID : setQuoteID }}</div>
              <div style="display: none;">{{ workingData.quoteID = savedData.quoteID }}</div>
              <input type="hidden" id="quoteID" name="quoteID" :value="workingData.quoteID">
            </div>

            <div class="c-form-row" v-if="userLevel === 'admin'">
              <label for="freightSurcharge" class="c-label">Freight (%)</label>
              <input type="number" class="c-input" id="freightSurcharge" name="freightSurcharge" :min="0" :max="100" v-model.number="workingData.freightSurcharge"  :disabled="!isInvalid ? false : true">
            </div>

            <div class="c-form-row">
              <label for="liftGate" class="c-label c-toggle" :class="[!isInvalid ? '' : '-is-disabled']">Lift Gate
                <input name="liftGate" id="liftGate" type="checkbox" v-model="workingData.liftGate" :true-value="1" :false-value="0" :disabled="!isInvalid ? false : true">
                <span class="c-toggle--toggler"></span>
                <span class="c-toggle--helper-text --left">No</span>
                <span class="c-toggle--helper-text --right">Yes</span>
              </label>
            </div>

            <div class="c-form-row" v-if="userLevel === 'admin'">
              <label for="liftGateUpcharge" class="c-label">Lift Gate Upcharge $</label>
              <input type="number" class="c-input" id="liftGateUpcharge" name="liftGateUpcharge" :min="0" v-model.number="workingData.liftGateUpcharge"  :disabled="!isInvalid ? false : true">
            </div>

            <div class="c-form-row">
              <label for="residentialDelivery" class="c-label c-toggle" :class="[!isInvalid ? '' : '-is-disabled']">Residential Delivery
                <input name="residentialDelivery" id="residentialDelivery" type="checkbox" v-model="workingData.residentialDelivery" :true-value="1" :false-value="0" :disabled="!isInvalid ? false : true">
                <span class="c-toggle--toggler"></span>
                <span class="c-toggle--helper-text --left">No</span>
                <span class="c-toggle--helper-text --right">Yes</span>
              </label>
            </div>

            <div class="c-form-row" v-if="userLevel === 'admin'">
              <label for="residentialDeliveryUpcharge" class="c-label">Residential Delivery Upcharge $</label>
              <input type="number" class="c-input" id="residentialDeliveryUpcharge" name="residentialDeliveryUpcharge" :min="0" v-model.number="workingData.residentialDeliveryUpcharge"  :disabled="!isInvalid ? false : true">
            </div>

            <div class="c-form-row">
              <label for="deliveryAppointment" class="c-label c-toggle" :class="[!isInvalid ? '' : '-is-disabled']">Delivery Appointment
                <input name="deliveryAppointment" id="deliveryAppointment" type="checkbox" v-model="workingData.deliveryAppointment" :true-value="1" :false-value="0" :disabled="!isInvalid ? false : true">
                <span class="c-toggle--toggler"></span>
                <span class="c-toggle--helper-text --left">No</span>
                <span class="c-toggle--helper-text --right">Yes</span>
              </label>
            </div>

            <div class="c-form-row" v-if="userLevel === 'admin'">
              <label for="deliveryAppointmentUpcharge" class="c-label">Delivery Appointment Upcharge $</label>
              <input type="number" class="c-input" id="deliveryAppointmentUpcharge" name="deliveryAppointmentUpcharge" :min="0" v-model.number="workingData.deliveryAppointmentUpcharge"  :disabled="!isInvalid ? false : true">
            </div>

            <div class="c-form-row" v-if="(totalUnits < workingData.minimumOrderThreshold) && userLevel === 'admin'">
              <label for="waiveMinimumOrderSurcharge" class="c-label c-toggle" :class="[!isInvalid ? '' : '-is-disabled']"><span class="c-toggle__text">Waive Minimum Order Surcharge</span>
                <input name="waiveMinimumOrderSurcharge" id="waiveMinimumOrderSurcharge" type="checkbox" v-model="workingData.waiveMinimumOrderSurcharge" true-value="yes" false-value="no" :disabled="!isInvalid ? false : true">
                <span class="c-toggle--toggler"></span>
                <span class="c-toggle--helper-text --left">No</span>
                <span class="c-toggle--helper-text --right">Yes</span>
                <button class="c-button c-button--trans-takeover" @click.prevent="waiveMinimumOrderSurcharge(workingData.waiveMinimumOrderSurcharge)" v-if="!isInvalid"></button>
              </label>
              <div style="display: none;">{{ workingData.waiveMinimumOrderSurcharge = workingData.waiveMinimumOrderSurcharge ? workingData.waiveMinimumOrderSurcharge : 'no' }}</div>
            </div>

            <div class="c-form-row">
              <label for="project" class="c-label">Project Name*</label>
              <input type="text" class="c-input" id="project" name="project" v-model="workingData.project" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
            </div>

            <div class="c-form-row">
              <label for="authorInput" class="c-label" style="text-transform: capitalize;">Prepared By*</label>
              <vSelect :options="workingData.groupUsers" v-model="workingData.author" :clearable="false" class="c-searchable-select" inputId="authorInput" :disabled="((workingData.groupUsers.length < 2 || userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false"></vSelect>
            </div>

            <div class="c-form-row">
              <label for="customerName" class="c-label">Prepared For</label>


              <div class="c-form-row -is-indented">
                <label for="customerName" class="c-label">Name</label>
                <input type="text" class="c-input" id="customerName" name="customerName" v-model="workingData.customerName" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
              </div>

              <div class="c-form-row -is-indented">
                <label for="emailAddress" class="c-label">Company</label>
                <input type="text" class="c-input" id="company" name="company" v-model="workingData.company" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
              </div>

              <div class="c-form-row -is-indented">
                <label for="emailAddress" class="c-label">Email</label>
                <input type="text" class="c-input" id="emailAddress" name="emailAddress" v-model="workingData.emailAddress" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
              </div>

              <div class="c-form-row -is-indented">
                <label for="customerPhone" class="c-label">Rep</label>
                <input type="text" class="c-input" id="customerPhone" name="customerPhone" v-model="workingData.customerPhone" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
              </div>
            </div>

            <div class="c-form-row">
              <label for="dateCreated" class="c-label">Date Created</label>
              <input type="text" class="c-input" :value="(workingData.dateQuoteUpdated ? workingData.dateQuoteUpdated : workingData.dateCreated.date) | formatDate" readonly :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
              <!-- <p>{{ formatDate(workingData.dateCreated.date) }}</p> -->
            </div>

            <div class="c-form-row">
              <label for="terms" class="c-label">Terms</label>
              <div>{{ workingData.terms !== '' ? workingData.terms.toUpperCase() : '50D/N30' }}</div>
            </div>

            <div class="c-form-row">
              <label for="shippingType" class="c-label">Freight</label>
              <input type="text" class="c-input" id="shippingType" name="shippingType" v-model="workingData.shippingType" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
              <div style="display: none;">{{ savedData.shippingType = savedData.shippingType ? savedData.shippingType : 'Listed, LTL to Dock' }}</div>
              <div style="display: none;">{{ workingData.shippingType = workingData.shippingType ? workingData.shippingType : savedData.shippingType }}</div>
            </div>

            <div class="c-form-row">
              <label for="shipTo" class="c-label">Ship to Address</label>
              <textarea cols="30" rows="5" class="c-input" id="shipTo" name="shipTo" v-model="workingData.shipTo" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false"></textarea>
            </div>

            <div class="c-form-row">
              <label for="leadTime" class="c-label">Lead Time</label>
              <input type="text" class="c-input" id="leadTime" name="leadTime" v-model="workingData.leadTime" :disabled="(userLevel === 'admin' && !isInvalid) ? false : true">
              <div style="display: none;">{{ savedData.leadTime = savedData.leadTime ? savedData.leadTime : 'Assigned (8 weeks - standard)' }}</div>
              <div style="display: none;">{{ workingData.leadTime = workingData.leadTime ? workingData.leadTime : savedData.leadTime }}</div>
            </div>

            <div class="c-form-row">
              <label for="salesTax" class="c-label">Estimated Sales Tax (%)</label>
              <input type="number" class="c-input" id="salesTax" name="salesTax" :min="0" :max="100" v-model.number="workingData.salesTax" :disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid) ? true : false">
            </div>
          </form>
        </div>
      </div>

      <div class="c-toolbar" v-if="dataLoaded">
        <div class="c-toolbar__inner">
          <button class="c-button c-button--primary c-button--toolbar" v-on:click="showQuoteDelete = 1" v-bind:disabled="((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts)) ? true : false">
            <svg width="13" height="16" xmlns="http://www.w3.org/2000/svg"><path d="M12.21 1.71c.165 0 .29.134.29.29a.282.282 0 0 1-.29.281h-.28L10.79 14.86c0 .625-.517 1.141-1.15 1.141H3.36a1.149 1.149 0 0 1-1.15-1.14L1.07 2.28H.79A.282.282 0 0 1 .5 2c0-.156.125-.29.29-.29h3.14v-.57C3.93.509 4.437 0 5.07 0h2.86c.633 0 1.14.508 1.14 1.14v.57h3.14zM4.5 1.14v.57h4v-.57a.574.574 0 0 0-.57-.57H5.07a.574.574 0 0 0-.57.57zm5.71 13.72L11.36 2.28H1.64L2.79 14.86c0 .313.25.57.57.57h6.28c.32 0 .57-.257.57-.57zM6.5 14.28A.287.287 0 0 1 6.21 14V3.71c0-.155.134-.28.29-.28.156 0 .29.125.29.28V14a.287.287 0 0 1-.29.281zm-1.984.008a.287.287 0 0 1-.297-.273l-.57-10.29a.28.28 0 0 1 .265-.296.288.288 0 0 1 .305.265l.57 10.29a.3.3 0 0 1-.273.304zm3.968 0a.3.3 0 0 1-.273-.305l.57-10.273a.288.288 0 0 1 .305-.266.288.288 0 0 1 .266.305l-.57 10.266a.287.287 0 0 1-.298.273z" fill="#00586F" fill-rule="evenodd"/></svg>
            Delete
          </button>
          <button class="c-button c-button--primary c-button--toolbar" @click.prevent="saveQuote" v-bind:disabled="saveDisabled || (isLoading === 1) || ((workingData.quoteType == 'quote' && userLevel == 'basic') || (userLevel !== 'admin' && workingData.approvalState === 'adminApprovalRequested') || (userLevel === 'sub-admin' && workingData.quoteType === 'quote' && hasCustomProducts) || isInvalid ? true : false)">
            <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg"><path d="M13.71 0L16 2.281V14.86c0 .625-.508 1.141-1.14 1.141H1.14C.509 16 0 15.484 0 14.86V1.14C0 .509.508 0 1.14 0h12.57zM4 6.281v-4.57h8v4.57c0 .32-.258.578-.57.578H4.57A.575.575 0 0 1 4 6.281zm.57-4v4h6.86v-4H4.57zm5.14 3.43V2.859h1.15v2.852H9.71zm5.72 9.148V2.57l-2-2H1.14a.574.574 0 0 0-.57.57v13.72c0 .312.258.57.57.57h1.15V9.14c0-.312.25-.57.57-.57h10.28c.32 0 .57.258.57.57v6.29h1.15c.312 0 .57-.258.57-.57zm-2.29.57V9.142H2.86v6.289h10.28zm-8.85-4.57a.288.288 0 1 1 0-.578h4c.155 0 .28.133.28.29 0 .156-.125.288-.28.288h-4zm5.14 0a.294.294 0 0 1-.29-.289c0-.156.133-.289.29-.289.156 0 .28.133.28.29 0 .156-.124.288-.28.288zM4.29 12.57a.284.284 0 0 1-.29-.289c0-.156.125-.281.29-.281h7.42c.165 0 .29.125.29.281 0 .164-.125.29-.29.29H4.29z" fill="#FFF" fill-rule="evenodd"/></svg>
            Save
          </button>
          <button class="c-button c-button--primary c-button--toolbar" @click.prevent="downloadQuote" :disabled='!saveDisabled || workingData.products.length <= 0'>
            <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><path d="M45.8 0H10C7.8 0 6 1.8 6 4v56c0 2.2 1.8 4 4 4h44c2.2 0 4-1.8 4-4V11.1L45.8 0zM47 6.5l3.8 3.5H47V6.5zM10 60V4h33v10h11v46H10z" fill="#FFF" fill-rule="evenodd"/><path d="M42.3 34.5l-2.6-3-5.7 5V18h-4v18.5l-5.7-5-2.6 3L32 43.7zM16 48h32v4H16z" fill="#FFF" fill-rule="evenodd"/></svg>
            Download
          </button>
          <button class="c-button c-button--primary c-button--toolbar" @click.prevent="exportQuote" :disabled='!saveDisabled || workingData.products.length <= 0' v-if="userLevel === 'admin'">
            <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><g fill="#fff"><path d="M47.987 21.938a.99.99 0 00-.053-.264c-.011-.032-.019-.063-.033-.094a.957.957 0 00-.193-.285l-.001-.001L42 15.586V10c0-.022-.011-.041-.013-.063a1.028 1.028 0 00-.051-.257c-.011-.032-.019-.063-.034-.094a.997.997 0 00-.196-.293l-9-9a.97.97 0 00-.294-.196c-.03-.014-.06-.022-.091-.032a.937.937 0 00-.263-.052C32.039.01 32.021 0 32 0H7a1 1 0 00-1 1v14.586L.293 21.293l-.002.002a.98.98 0 00-.192.285c-.014.031-.022.062-.033.094a.953.953 0 00-.053.264C.011 21.96 0 21.978 0 22v19a1 1 0 001 1h5v5a1 1 0 001 1h34a1 1 0 001-1v-5h5a1 1 0 001-1V22c0-.022-.011-.04-.013-.062zM44.586 21H42v-2.586L44.586 21zm-6-12H33V3.414L38.586 9zM8 2h23v8a1 1 0 001 1h8v10H8V2zM6 18.414V21H3.414L6 18.414zM40 46H8v-4h32v4zm6-6H2V23h44v17z"/><path d="M23.422 27.885c.147-.136.312-.235.493-.298a1.72 1.72 0 01.561-.093c.669 0 1.224.266 1.666.799l1.122-1.462a3.164 3.164 0 00-1.215-.876c-.482-.198-1.028-.297-1.64-.297-.419 0-.833.071-1.241.212-.408.142-.774.36-1.097.655a3.285 3.285 0 00-.782 1.113c-.198.448-.298.984-.298 1.607 0 .499.065.926.195 1.283.13.358.306.669.527.935.221.267.476.496.765.689.289.193.598.368.927.527.521.261.952.544 1.292.85.34.306.51.72.51 1.241 0 .533-.142.946-.425 1.241s-.64.442-1.071.442c-.385 0-.762-.091-1.131-.272s-.683-.431-.944-.748l-1.105 1.496c.34.397.793.725 1.36.986a4.375 4.375 0 001.853.391c.465 0 .907-.079 1.326-.238a3.076 3.076 0 001.097-.706 3.46 3.46 0 00.748-1.147c.187-.453.281-.975.281-1.564 0-.51-.079-.952-.238-1.326a3.376 3.376 0 00-.612-.969 4.065 4.065 0 00-.833-.697 10.324 10.324 0 00-.901-.51c-.499-.249-.901-.513-1.207-.791-.306-.277-.459-.671-.459-1.181 0-.295.042-.55.128-.765.085-.215.201-.391.348-.527zM15.177 28.854c.204-.397.459-.711.765-.944a1.723 1.723 0 011.071-.348c.737 0 1.337.334 1.802 1.003l1.173-1.428a3.284 3.284 0 00-1.275-1.097c-.521-.255-1.128-.382-1.819-.382-.669 0-1.278.156-1.828.467a4.229 4.229 0 00-1.403 1.309c-.385.562-.683 1.23-.892 2.006a9.72 9.72 0 00-.314 2.542c0 .918.105 1.762.314 2.533s.504 1.436.884 1.997c.38.562.844 1.001 1.394 1.318s1.165.476 1.845.476c.748 0 1.368-.147 1.861-.442s.898-.68 1.215-1.156l-1.173-1.377c-.193.295-.434.544-.722.748s-.632.306-1.028.306c-.419 0-.785-.116-1.097-.349-.312-.232-.569-.546-.774-.943s-.357-.864-.459-1.403a9.158 9.158 0 01-.153-1.708c0-.612.051-1.187.153-1.726a5.199 5.199 0 01.46-1.402zM32.007 35.62h-.051l-2.006-9.656h-2.278L30.613 38h2.584l2.992-12.036h-2.176z"/></g></svg>
            Export CSV
          </button>
          <!-- Admin quote approval/deny buttons for Admins -->
          <div class="c-toolbar__inner--float-right" v-if="(userLevel === 'admin' && hasCustomProducts && workingData.quoteType === 'estimate' && workingData.approvalState === 'adminApprovalRequested') && !isInvalid">
            <div class="c-approval-process__date">
              Review and make your decision
            </div>
            <div class="c-approval-process__message">
              <textarea cols="30" rows="5" class="c-input" id="approvalMessage" name="approvalMessage" v-model="workingData.approvalMessage" placeholder="Approval Message (optional)"></textarea>
            </div>
            <button class="c-button c-button--primary c-button--toolbar c-approval-process__button --approve" @click.prevent="adminApproveQuote">
              Approve
            </button>
            <button class="c-button c-button--primary c-button--toolbar c-approval-process__button --deny" @click.prevent="adminDenyQuote">
              Deny
            </button>
          </div>
          <!-- Admin quote approval buttons for Reps -->
          <div class="c-toolbar__inner--float-right" v-if="(userLevel === 'sub-admin' && hasCustomProducts && workingData.quoteType === 'estimate') && !isInvalid">
            <div class="c-approval-process__date" v-if="workingData.lastApprovalRequestDate !== null">
              Last submitted {{ workingData.lastApprovalRequestDate }}
            </div>
            <button class="c-button c-button--primary c-button--toolbar c-approval-process__button" @click.prevent="convertQuote(workingData.quoteType)">
              Request Quote Approval
            </button>
          </div>
          <!-- Submit for Quote for basic users -->
          <div class="c-toolbar__inner--float-right" v-if="(userLevel === 'basic' && workingData.quoteType === 'estimate') && !isInvalid">
            <div class="c-approval-process__date" v-if="workingData.lastApprovalRequestDate !== null">
              Last submitted {{ workingData.lastApprovalRequestDate }}
            </div>
            <button class="c-button c-button--primary c-button--toolbar c-approval-process__button" @click.prevent="submitForQuote()" v-bind:disabled="isLoading === 1">
              Submit for Quote
            </button>
          </div>
          <!-- Update pricing for out of date estimates/quotes -->
          <div class="c-toolbar__inner--float-right" v-if="isInvalid">
            <div class="c-approval-process__date">
              Unlock your {{ workingData.quoteType == 'estimate' ? 'estimate' : 'quote' }} with a pricing update
            </div>
            <button class="c-button c-button--primary c-button--toolbar c-approval-process__button" @click.prevent="updatePricing">
              Update {{ workingData.quoteType == 'estimate' ? 'Estimate' : 'Quote' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>

</style>

<script>
import axios from 'axios';
import currency from 'currency.js';
import { format, parse } from 'date-fns';
import Datepicker from 'vuejs-datepicker';
import {_, isEqual} from 'vue-underscore';
import Loader from './Loader';
import vSelect from 'vue-select';
import { print } from 'graphql';
import gql from 'graphql-tag';

const apiToken = 'sGev6T821A4zxzZvxpjpqgoN4e7LIcRB';
const apiUrl = '/api';
const saveQuoteMutation = gql`
    mutation quoteMutation(
        $id: ID,
        $quoteID: String,
        $title: String,
        $customerName: String,
        $company: String,
        $customerContact: String,
        $emailAddress: String,
        $customerPhone: String,
        $project: String,
        $address: String,
        $terms: String,
        $shippingType: String,
        $shipTo: String,
        $specifiedBy: String,
        $dealer: String,
        $architect: String,
        $contact: String,
        $shipDate: String,
        $createdBy: String,
        $leadTime: String,
        $notes: String,
        $productsJSON: productsJSON_MatrixInput,
        $salesTax: Number,
        $freightSurcharge: Number,
        $liftGate: Number,
        $liftGateUpcharge: Number,
        $residentialDelivery: Number,
        $residentialDeliveryUpcharge: Number,
        $deliveryAppointment: Number,
        $deliveryAppointmentUpcharge: Number,
        $totalListPrice: Number,
        $totalTextilePrice: Number,
        $totalNetPrice: Number,
        $totalTaxPrice: Number,
        $totalQuotePrice: Number,
        $minimumOrderSurcharge: Number,
        $minimumOrderThreshold: Number,
        $lastSavedDiscount: Number,
        $quoteType: String,
        $waiveMinimumOrderSurcharge: String,
        $authorId: ID,
        $lastApprovalRequestDate: String,
        $approvalState: String,
        $approvalMessage: String,
        $dateQuoteUpdated: String,
        $validUntilDate: String,
        $lastSaveBy: [Int]
    ) {
        save_quotes_quotes_Entry(
            id: $id,
            quoteID: $quoteID,
            title: $title,
            customerName: $customerName,
            company: $company,
            customerContact: $customerContact,
            emailAddress: $emailAddress,
            customerPhone: $customerPhone,
            project: $project,
            address: $address,
            terms: $terms,
            shippingType: $shippingType,
            shipTo: $shipTo,
            specifiedBy: $specifiedBy,
            dealer: $dealer,
            architect: $architect,
            contact: $contact,
            shipDate: $shipDate,
            createdBy: $createdBy,
            leadTime: $leadTime,
            notes: $notes,
            productsJSON: $productsJSON,
            salesTax: $salesTax,
            freightSurcharge: $freightSurcharge,
            liftGate: $liftGate,
            liftGateUpcharge: $liftGateUpcharge,
            residentialDelivery: $residentialDelivery,
            residentialDeliveryUpcharge: $residentialDeliveryUpcharge,
            deliveryAppointment: $deliveryAppointment,
            deliveryAppointmentUpcharge: $deliveryAppointmentUpcharge,
            totalListPrice: $totalListPrice,
            totalTextilePrice: $totalTextilePrice,
            totalNetPrice: $totalNetPrice,
            totalTaxPrice: $totalTaxPrice,
            totalQuotePrice: $totalQuotePrice,
            minimumOrderSurcharge: $minimumOrderSurcharge,
            minimumOrderThreshold: $minimumOrderThreshold,
            lastSavedDiscount: $lastSavedDiscount,
            quoteType: $quoteType,
            waiveMinimumOrderSurcharge: $waiveMinimumOrderSurcharge,
            authorId: $authorId,
            lastApprovalRequestDate: $lastApprovalRequestDate,
            approvalState: $approvalState,
            approvalMessage: $approvalMessage,
            dateQuoteUpdated: $dateQuoteUpdated,
            validUntilDate: $validUntilDate,
            lastSaveBy: $lastSaveBy
        ) {
            id
        }
    }
`;

// Delete mutation
const deleteQuoteMutation = gql`
    mutation quoteMutation(
      $id: Int!
    ) {
        deleteEntry(
          id: $id
        )
    }
`;

const configureApi = (url, token) => {
  return {
    baseURL: url,
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Requested-With': 'XMLHttpRequest'
    }
  };
};
 // Execute a GraphQL query by sending an XHR to our api endpoint
const executeMutation = (api, query, variables, callback) => {
  api.post('', {
    query: print(query),
    variables: variables
  }).then((result) => {
    if (callback) {
      callback(result.data);
    }
  }).catch((error) => {
    console.log(error);
  })
};

const candyProductBaseCodes = [
  'CB01-10-ST-24',
  'CB01-10-ST-48',
  'CB01-10-ST-60',
  'CB01-02-OC-R66',
  'CB01-02-OC-R80',
  'CB01-01-IC-R28',
  'CB01-01-IC-R40',
  'CB01-03-EN-180',
  'CB01-T1-ST-12',
  'CB01-T1-ST-24',
  'CB01-T1-ST-48',
  'CB01-T2-CR-90',
  'CB01-T3-EN-180'
];

const currencyFormat = {
  symbol: true
}

export default {
  name: 'QuoteOverview',
  props: ['quote-id', 'user-level', 'user-id', 'is-invalid'],
  components: {
    Datepicker,
    Loader,
    vSelect
  },
  data() {
    return {
      quoteApi: axios.create(configureApi(apiUrl, apiToken)),
      dataLoaded: false,
      isLoading: 1,
      saveDisabled: true,
      savedData: [],
      workingData: [],
      productsSortOrder: [],
      formattedProductData: {},
      showQuoteDelete: 0,
      showQuoteConvert: 0
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    normalizeProductConfiguration(product) {
      const arrayFields = [
        'species',
        'moduleOne',
        'moduleTwo',
        'moduleThree',
        'moduleFour',
        'moduleFive',
        'armHeight',
        'tableTopSize',
        'armCapMaterial',
        'pads',
        'plinthType',
        'plinthMaterials',
        'plinthLaminateBrands',
        'plinthFinishes',
        'tables',
        'partitions',
        'powerModules',
        'tableTopMaterial',
        'tableTopMaterialFinishSideA',
        'tableTopMaterialFinishSideB',
        'tableTopMaterialForboFinish',
        'tableTopMaterialMetalFinish',
        'largeInsertTopMaterial',
        'largeInsertTopMaterialForboFinish',
        'largeInsertTopMaterialSolidSurfaceFinish',
        'smallInsertTopMaterial',
        'smallInsertTopMaterialForboFinish',
        'smallInsertTopMaterialSolidSurfaceFinish',
        'caseworkSurfaceMaterial',
        'columnShroudPaint',
        'legMaterial',
        'metalFinish',
        'woodFinish',
        'paintedWood',
        'stoneQuartzTableTop',
        'hingesOne',
        'hingesTwo',
        'hingesThree',
        'pull',
        'wrap',
        'veneerOuterCase',
        'multTextilesOption',
        'singleTextileGrade',
        'armTextileGrade',
        'armBackTextileGrade',
        'armSeatTextileGrade',
        'backTextileGrade',
        'seatTextileGrade',
        'seatBackTextileGrade',
        'sideATextileGrade',
        'sideBTextileGrade'
      ];

      if (!product || typeof product !== 'object') {
        return {};
      }

      arrayFields.forEach((field) => {
        if (!Array.isArray(product[field])) {
          product[field] = [];
        }
      });

      return product;
    },
    normalizeQuoteProducts(quoteData) {
      if (!quoteData || !Array.isArray(quoteData.products)) {
        return quoteData;
      }

      quoteData.products = quoteData.products.map((item) => {
        if (item && item.product) {
          item.product = this.normalizeProductConfiguration(item.product);
        }

        return item;
      });

      return quoteData;
    },
    fetchData() {
      var vm = this;
      vm.isLoading = 1;
      if (this.userLevel === 'admin') {
          this.adminQuery = '?isAdmin=1';
      } else {
          this.adminQuery = '';
      }
      // Append if current user is an admin
      axios.get(`/quote/${this.quoteId}.json${this.adminQuery}`)
        .then(function (response) {
          const normalizedData = vm.normalizeQuoteProducts(JSON.parse(JSON.stringify(response.data)));
          vm.savedData = JSON.parse(JSON.stringify(normalizedData));
          vm.workingData = normalizedData;
          vm.dataLoaded = true;
        })
        .catch(function (error) {
          console.log(error);
        })
        .then(function () {
          vm.isLoading = 0;
        });
    },
    deleteProduct: function(x) {
      this.workingData.products.splice(x, 1);
    },
    downloadQuote () {
      var pdfUrl = window.location.href + "/pdf"
      window.open(pdfUrl, '_blank');
    },
    exportQuote () {
      var csvUrl = window.location.href + "/csv"
      window.open(csvUrl, '_blank');
    },
    isMaru(x) {
        // Create array of Maru product base code(s)
        const maruProducts = [
                                'SG03-IND-S7-86W',
                                'SG03-IND-S7-86W-BC4-BG4',
                                'SG03-IND-S7-86W-BE4',
                                'SG03-IND-S7-86W-BC4R-BG4R',
                                'SG03-IND-S5-86W',
                                'SG03-IND-S5-86W-BC2-BG2',
                                'SG03-IND-S5-86W-BC2-BG4',
                                'SG03-IND-S5-86W-BC4-BG2',
                                'SG03-IND-S5-86W-BC2',
                                'SG03-IND-S5-86W-BG2',
                                'SG03-IND-S3-86W',
                                'SG03-IND-S3-66W',
                                'SG03-OUT-S7-86W',
                                'SG03-OUT-S7-86W-BC4-BG4',
                                'SG03-OUT-S7-86W-BE4',
                                'SG03-OUT-S7-86W-BC4R-BG4R',
                                'SG03-OUT-S5-86W',
                                'SG03-OUT-S5-86W-BC2-BG2',
                                'SG03-OUT-S5-86W-BC2-BG4',
                                'SG03-OUT-S5-86W-BC4-BG2',
                                'SG03-OUT-S5-86W-BC2',
                                'SG03-OUT-S5-86W-BG2',
                                'SG03-OUT-S3-86W',
                                'SG03-OUT-S3-66W',
                                'SG03-IND-BOL-40W',
                                'SG03-OUT-BOL-40W',
                                'SG03-IND-PAD-40W',
                                'SG03-OUT-PAD-40W',
                                'SG03-IND-PAD-60W',
                                'SG03-OUT-PAD-60W',
                                'SG03-IND-T1',
                                'SG03-IND-T2',
                                'SG03-IND-SCR1',
                                'SG03-OUT-SCR1',
                                'SG03-IND-PM',
                                'SG03-IND-PS'
                            ];

        // If the current product is found in the Maru Products array, return a
        // true result, on call of isMaru()
        if (maruProducts.includes(x)) {

            // Product found
            return true;
        } else {

            // Product not found
            return false;
        }
    },
    isApril(x) {
        // Create array of April product base code(s)
        const aprilProducts = [
                                'JY02-10',
                                'JY02-16',
                                'JY02-17'
                            ];

        // If the current product is found in the April Products array, return a
        // true result, on call of isApril()
        if (aprilProducts.includes(x)) {

            // Product found
            return true;
        } else {

            // Product not found
            return false;
        }
    },
    isProse(x) {
        // Create array of Prose product base code(s)
        const proseProducts = [
                                'GC01-T1',
                                'GC01-T2',
                                'GC01-01',
                                'GC01-02',
                                'GC01-10',
                                'GC01-11',
                                'GC01-15',
                                'GC01-22'
                            ];

        // If the current product is found in the Prose Products array, return a
        // true result, on call of isProse()
        if (proseProducts.includes(x)) {

            // Product found
            return true;
        } else {

            // Product not found
            return false;
        }
    },
    isBSide(x) {
        // Create array of B-Side product base code(s)
        const bSideProducts = [
                                'MM03-T1',
                                'MM03-T2',
                                'MM03-T3'
                            ];

        // If the current product is found in the B-Side Products array, return a
        // true result, on call of isBSide()
        if (bSideProducts.includes(x)) {

            // Product found
            return true;
        } else {

            // Product not found
            return false;
        }
    },
    saveQuote() {
      this.isLoading = 1;

      // Save a copy of the current data for cleaning and saving
      const productData = JSON.parse(JSON.stringify(this.workingData.products));

      // Remove optionName nodes from product data for saving to craft db
      productData.map((item, index) => {

          // Set unique block id
          var blockId = 'new:'+(index+1);

          // Add block id to product sort order
          this.productsSortOrder.push(blockId);

          // Set product block
          var productBlock  = {
              'product': {
                'configuration': JSON.stringify(item.product)
              }
          }

          // Push block to formatted product data
          this.formattedProductData[blockId] = productBlock;
      });

      // Set up variables for saving
      const variables = {
          'id': this.quoteId,
          'quoteID': String(this.workingData.quoteID),
          'title': this.workingData.customerName+' - '+this.workingData.project,
          'project': this.workingData.project,
          'authorId': this.workingData.author.id,
          'customerName': this.workingData.customerName,
          'company': this.workingData.company,
          'emailAddress': this.workingData.emailAddress,
          'customerPhone': this.workingData.customerPhone,
          'terms': this.workingData.terms,
          'shippingType': this.workingData.shippingType,
          'shipTo': this.workingData.shipTo,
          'leadTime': this.workingData.leadTime,
          'salesTax': this.workingData.salesTax,
          'freightSurcharge': this.workingData.freightSurcharge,
          'liftGate': this.workingData.liftGate,
          'liftGateUpcharge': this.workingData.liftGateUpcharge,
          'residentialDelivery': this.workingData.residentialDelivery,
          'residentialDeliveryUpcharge': this.workingData.residentialDeliveryUpcharge,
          'deliveryAppointment': this.workingData.deliveryAppointment,
          'deliveryAppointmentUpcharge': this.workingData.deliveryAppointmentUpcharge,
          'customerContact': this.workingData.customerContact,
          'address': this.workingData.address,
          'specifiedBy': this.workingData.specifiedBy,
          'dealer': this.workingData.dealer,
          'architect': this.workingData.architect,
          'contact': this.workingData.contact,
          'shipDate': this.workingData.shipDate,
          'createdBy': this.workingData.createdBy,
          'notes': this.workingData.notes,
          'productsJSON': {
              sortOrder: this.productsSortOrder,
              blocks: this.formattedProductData,
          },
          'totalListPrice': this.workingData.totalListPrice,
          'totalTextilePrice': this.workingData.totalTextilePrice,
          'totalNetPrice': this.workingData.totalNetPrice,
          'totalTaxPrice': this.workingData.totalTaxPrice,
          'totalQuotePrice': this.workingData.totalQuotePrice,
          'minimumOrderSurcharge': this.workingData.minimumOrderSurcharge,
          'minimumOrderThreshold': this.workingData.minimumOrderThreshold,
          'lastSavedDiscount': this.workingData.lastSavedDiscount,
          'quoteType': this.workingData.quoteType,
          'waiveMinimumOrderSurcharge': this.workingData.waiveMinimumOrderSurcharge,
          'lastApprovalRequestDate': this.workingData.lastApprovalRequestDate,
          'approvalState': this.workingData.approvalState,
          'approvalMessage': this.workingData.approvalMessage,
          'dateQuoteUpdated': this.workingData.dateQuoteUpdated,
          'validUntilDate': this.workingData.validUntilDate,
          'lastSaveBy': parseFloat(this.userId)
      }
      // Execute the query
      executeMutation(this.quoteApi, saveQuoteMutation, variables, (data) => {
          this.submitted = true;
          this.saveDisabled = true;
          console.log('submitted');
          window.location.replace('/quote/edit/'+this.quoteId);
      });
    },
    deleteQuote() {
      // Reset working data to ensure no changes are present. This will help us
      // against built in browser alerts informing a user that their changes have
      // not been saved. We do not need to save these changes though, since we're
      // attempting to delete. Let's instead set working data to match saved data
      this.workingData = this.savedData;

      // Set up variables for saving
      const deleteVariables = {
          'id': this.quoteId
      }
      // Execute the query
      executeMutation(this.quoteApi, deleteQuoteMutation, deleteVariables, (data) => {
          this.submitted = true;
          console.log('submitted');
          window.location.replace('/quotes');
      });
      // Hide delete confirmation window
      this.showQuoteDelete = 0;
      // Show loading progress
      this.isLoading = 1;
    },
    waiveMinimumOrderSurcharge(x) {
      // If user is admin or sub-admin without any custom products
      if (this.userLevel === 'admin') {
        // If no
        if (x === 'no') {
          this.workingData.waiveMinimumOrderSurcharge = 'yes';
        }
        // If yes
        if (x === 'yes') {
          this.workingData.waiveMinimumOrderSurcharge = 'no';
        }
      }
    },
    convertQuote(x) {
      // If user is admin or sub-admin without any custom products
      if (this.userLevel === 'admin' || (this.userLevel === 'sub-admin' && !this.hasCustomProducts)) {
        // If estimate
        if (x === 'estimate') {
          this.workingData.quoteType = 'quote';
        }
        // If quote
        if (x === 'quote') {
          this.workingData.quoteType = 'estimate';
        }
      }
      // If user is sub-admin with custom products
      if (this.userLevel === 'sub-admin' && this.hasCustomProducts) {
        // If estimate
        if (x === 'estimate') {
          // Let's get the current date
          var currDate = new Date();

          // Let's parse date to our preferred format
          currDate = parse(currDate).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

          // Now let's pass on and set our date to the current state of approval
          this.workingData.lastApprovalRequestDate = currDate;
          this.workingData.approvalState = 'adminApprovalRequested';
        }
        // If quote
        if (x === 'quote') {
          this.workingData.quoteType = 'estimate';
        }
      }
      // Close takeover modal
      this.showQuoteConvert = 0;
      // Save quote with change
      this.saveQuote();
    },
    submitForQuote() {
        // If user is basic, keep going
        if (this.userLevel === 'basic') {
          // Let's get the current date
          var currDate = new Date();

          // Let's parse date to our preferred format
          currDate = parse(currDate).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

          // Now let's pass on and set our date to the current state of approval
          this.workingData.lastApprovalRequestDate = currDate;
          this.workingData.approvalState = 'submitForQuoteRequested';

          // Save estimate/quote with change
          this.saveQuote();
        }
    },
    updatePricing() {
      // Show loading screen
      this.isLoading = 1;
      this.workingData = this.normalizeQuoteProducts(this.workingData);
      // Preserve current freight surcharge to avoid resetting on refresh
      const existingFreightSurcharge = this.workingData.freightSurcharge;
      const existingLiftGateUpcharge = this.workingData.liftGateUpcharge;
      const existingResidentialDeliveryUpcharge = this.workingData.residentialDeliveryUpcharge;
      const existingDeliveryAppointmentUpcharge = this.workingData.deliveryAppointmentUpcharge;
      // Establish data parent
      if (this.workingData.products.length > 0) {
        var _data = this;
        // Load textile pricing
        axios.get(`/textiles.json`)
          .then(function (response) {
            // Set textile pricing data
            _data.textileData = response.data;
            // Initiate our counter
            let count = 0;
            // Update product pricing
            _data.workingData.products.map((item, i) => {
              // Load product data
              axios.get(`/product/${item.product.productID}.json`)
                .then(function (response) {
                  _data.productData = response.data;
                })
                .catch(function (error) {
                  console.log(error);
                })
                .then(function () {
                  // Only run on non-custom products
                  if (item.product.productSlug !== 'custom-product') {
                    // Update product pricing values
                    item.product.baseCom = currency(_data.productData.baseCom);
                    item.product.priceTAN = currency(_data.productData.pricing.TAN);
                    item.product.priceSET = currency(_data.productData.pricing.SET);
                    item.product.priceINL = currency(_data.productData.pricing.INL);
                    item.product.priceIA = currency(_data.productData.pricing.IA);
                    item.product.priceXX = currency(_data.productData.pricing.XX);
                    item.product.priceWAC = currency(_data.productData.pricing.WAC);
                    item.product.priceCA1 = currency(_data.productData.pricing.CA1);
                    item.product.priceCA2 = currency(_data.productData.pricing.CA2);
                    item.product.priceCA3 = currency(_data.productData.pricing.CA3);
                    item.product.priceCA4 = currency(_data.productData.pricing.CA4);
                    item.product.priceCA5 = currency(_data.productData.pricing.CA5);
                    item.product.priceCA6 = currency(_data.productData.pricing.CA6);
                    item.product.priceCA7 = currency(_data.productData.pricing.CA7);
                    item.product.priceLTT = currency(_data.productData.pricing.LTT);
                    item.product.priceFTT = currency(_data.productData.pricing.FTT);
                    item.product.priceWTT = currency(_data.productData.pricing.WTT);
                    item.product.priceWTA = currency(_data.productData.pricing.WTA);
                    item.product.priceWTB = currency(_data.productData.pricing.WTB);
                    item.product.priceWTW = currency(_data.productData.pricing.WTW);
                    item.product.priceVTA = currency(_data.productData.pricing.VTA);
                    item.product.priceVTW = currency(_data.productData.pricing.VTW);
                    item.product.priceUPH = currency(_data.productData.pricing.UPH);
                    item.product.priceCT1 = currency(_data.productData.pricing.CT1);
                    item.product.priceCT2 = currency(_data.productData.pricing.CT2);
                    item.product.priceCT3 = currency(_data.productData.pricing.CT3);
                    item.product.priceCT4 = currency(_data.productData.pricing.CT4);
                    item.product.priceCT5 = currency(_data.productData.pricing.CT5);
                    item.product.priceCT6 = currency(_data.productData.pricing.CT6);
                    item.product.priceCT7 = currency(_data.productData.pricing.CT7);
                    item.product.priceSTT = currency(_data.productData.pricing.STT);
                    item.product.priceMTT = currency(_data.productData.pricing.MTT);
                    item.product.priceWCW = currency(_data.productData.pricing.WCW);
                    item.product.priceWCA = currency(_data.productData.pricing.WCA);
                    item.product.priceWGA = currency(_data.productData.pricing.WGA);
                    item.product.priceWOA = currency(_data.productData.pricing.WOA);
                    item.product.priceWWA = currency(_data.productData.pricing.WWA);
                    item.product.priceWYA = currency(_data.productData.pricing.WYA);
                    item.product.priceWLA = currency(_data.productData.pricing.WLA);
                    item.product.priceWNP = currency(_data.productData.pricing.WNP);
                    item.product.priceCS1 = currency(_data.productData.pricing.CS1);
                    item.product.priceCS2 = currency(_data.productData.pricing.CS2);
                    item.product.priceCS3 = currency(_data.productData.pricing.CS3);
                    item.product.priceCS4 = currency(_data.productData.pricing.CS4);
                    item.product.priceCS5 = currency(_data.productData.pricing.CS5);
                    item.product.priceCS6 = currency(_data.productData.pricing.CS6);
                    item.product.priceCS7 = currency(_data.productData.pricing.CS7);
                    item.product.priceLM = currency(_data.productData.pricing.LM);
                    item.product.priceLW = currency(_data.productData.pricing.LW);
                    item.product.priceMLG = currency(_data.productData.pricing.MLG);
                    item.product.priceMSG = currency(_data.productData.pricing.MSG);
                    item.product.priceMMB = currency(_data.productData.pricing.MMB);
                    item.product.priceMDG = currency(_data.productData.pricing.MDG);
                    item.product.priceMSM = currency(_data.productData.pricing.MSM);
                    item.product.priceMNB = currency(_data.productData.pricing.MNB);
                    item.product.priceMYL = currency(_data.productData.pricing.MYL);
                    item.product.priceMGG = currency(_data.productData.pricing.MGG);
                    item.product.priceMCR = currency(_data.productData.pricing.MCR);
                    item.product.priceMSS = currency(_data.productData.pricing.MSS);
                    item.product.priceSBF = currency(_data.productData.pricing.SBF);
                    item.product.priceSBR = currency(_data.productData.pricing.SBR);
                    item.product.priceL002 = currency(_data.productData.pricing.L002);
                    item.product.priceL003 = currency(_data.productData.pricing.L003);
                    item.product.priceL007 = currency(_data.productData.pricing.L007);
                    item.product.priceL009 = currency(_data.productData.pricing.L009);
                    item.product.priceL027 = currency(_data.productData.pricing.L027);
                    item.product.priceL032 = currency(_data.productData.pricing.L032);
                    item.product.priceMB = currency(_data.productData.pricing.MB);
                    item.product.priceRC = currency(_data.productData.pricing.RC);
                    item.product.priceFB = currency(_data.productData.pricing.FB);
                    item.product.priceCAS = currency(_data.productData.pricing.CAS);
                    item.product.priceAP = currency(_data.productData.pricing.AP);
                    item.product.priceDJ = currency(_data.productData.pricing.DJ);
                    item.product.priceHL1 = currency(_data.productData.pricing.HL1);
                    item.product.priceHR1 = currency(_data.productData.pricing.HR1);
                    item.product.priceHL2 = currency(_data.productData.pricing.HL2);
                    item.product.priceHR2 = currency(_data.productData.pricing.HR2);
                    item.product.priceHL3 = currency(_data.productData.pricing.HL3);
                    item.product.priceHR3 = currency(_data.productData.pricing.HR3);
                    item.product.priceP1 = currency(_data.productData.pricing.P1);
                    item.product.priceP2 = currency(_data.productData.pricing.P2);
                    item.product.priceP3 = currency(_data.productData.pricing.P3);
                    item.product.priceFW = currency(_data.productData.pricing.FW);
                    item.product.priceWL = currency(_data.productData.pricing.WL);
                    item.product.priceWR = currency(_data.productData.pricing.WR);
                    item.product.priceNW = currency(_data.productData.pricing.NW);
                    item.product.priceDL = currency(_data.productData.pricing.DL);
                    item.product.priceVA = currency(_data.productData.pricing.VA);
                    item.product.priceVW = currency(_data.productData.pricing.VW);
                    item.product.priceTD = currency(_data.productData.pricing.TD);
                    item.product.priceAS = currency(_data.productData.pricing.AS);
                    item.product.priceFH = currency(_data.productData.pricing.FH);
                    item.product.priceGH = currency(_data.productData.pricing.GH);
                    item.product.priceTA = currency(_data.productData.pricing.TA);
                    item.product.priceAT = currency(_data.productData.pricing.AT);
                    item.product.priceST = currency(_data.productData.pricing.ST);
                    item.product.priceBT = currency(_data.productData.pricing.BT);
                    item.product.priceAB = currency(_data.productData.pricing.AB);
                    item.product.priceGRCOM = currency(_data.textileData.priceGRCOM);
                    item.product.priceGR01 = currency(_data.textileData.priceGR01);
                    item.product.priceGR02 = currency(_data.textileData.priceGR02);
                    item.product.priceGR03 = currency(_data.textileData.priceGR03);
                    item.product.priceGR04 = currency(_data.textileData.priceGR04);
                    item.product.priceGR05 = currency(_data.textileData.priceGR05);
                    item.product.priceGR06 = currency(_data.textileData.priceGR06);
                    item.product.priceGR07 = currency(_data.textileData.priceGR07);
                    item.product.priceGR08 = currency(_data.textileData.priceGR08);
                    item.product.priceGR09 = currency(_data.textileData.priceGR09);
                    item.product.priceGR10 = currency(_data.textileData.priceGR10);
                    item.product.priceGR11 = currency(_data.textileData.priceGR11);
                    item.product.priceGR12 = currency(_data.textileData.priceGR12);
                    item.product.priceGR13 = currency(_data.textileData.priceGR13);
                    item.product.priceGR14 = currency(_data.textileData.priceGR14);
                    item.product.priceGR15 = currency(_data.textileData.priceGR15);
                    item.product.priceGR16 = currency(_data.textileData.priceGR16);
                    item.product.priceGR17 = currency(_data.textileData.priceGR17);
                    item.product.priceGR18 = currency(_data.textileData.priceGR18);
                    item.product.priceGR19 = currency(_data.textileData.priceGR19);
                    item.product.priceGR20 = currency(_data.textileData.priceGR20);
                    item.product.priceB = currency(_data.productData.pricing.B);
                    item.product.priceA = currency(_data.productData.pricing.A);
                    item.product.priceW = currency(_data.productData.pricing.W);
                    item.product.priceOM = currency(_data.productData.pricing.OM);
                    item.product.priceLA = currency(_data.productData.pricing.LA);
                    item.product.priceMA = currency(_data.productData.pricing.MA);
                    item.product.priceHA = currency(_data.productData.pricing.HA);
                    item.product.priceLTL = currency(_data.productData.pricing.LTL);
                    item.product.priceFTL = currency(_data.productData.pricing.FTL);
                    item.product.priceSSL = currency(_data.productData.pricing.SSL);
                    item.product.priceVAL = currency(_data.productData.pricing.VAL);
                    item.product.priceVWL = currency(_data.productData.pricing.VWL);
                    item.product.priceXXS = currency(_data.productData.pricing.XXS);
                    item.product.priceLTS = currency(_data.productData.pricing.LTS);
                    item.product.priceFTS = currency(_data.productData.pricing.FTS);
                    item.product.priceSSS = currency(_data.productData.pricing.SSS);
                    item.product.priceVAS = currency(_data.productData.pricing.VAS);
                    item.product.priceVWS = currency(_data.productData.pricing.VWS);
                    item.product.priceSTS = currency(_data.productData.pricing.STS);
                    item.product.priceRWM = currency(_data.productData.pricing.RWM);
                    item.product.priceRVQ = currency(_data.productData.pricing.RVQ);
                    item.product.priceRCQ = currency(_data.productData.pricing.RCQ);
                    item.product.priceRIQ = currency(_data.productData.pricing.RIQ);
                    item.product.priceROQ = currency(_data.productData.pricing.ROQ);
                    item.product.priceTSS = currency(_data.productData.pricing.TSS);
                    item.product.priceTSM = currency(_data.productData.pricing.TSM);
                    item.product.priceTSL = currency(_data.productData.pricing.TSL);
                    item.product.pricePC2PC6PG2PG6 = currency(_data.productData.pricing.PC2PC6PG2PG6);
                    item.product.pricePB4RPE2PE6PH4R = currency(_data.productData.pricing.PB4RPE2PE6PH4R);
                    item.product.pricePB4RPD4RPF4RPH4R = currency(_data.productData.pricing.PB4RPD4RPF4RPH4R);
                    item.product.pricePC4PG4 = currency(_data.productData.pricing.PC4PG4);
                    item.product.pricePC4PG2 = currency(_data.productData.pricing.PC4PG2);
                    item.product.pricePC2PG4 = currency(_data.productData.pricing.PC2PG4);
                    item.product.pricePC4 = currency(_data.productData.pricing.PC4);
                    item.product.pricePG4 = currency(_data.productData.pricing.PG4);
                    item.product.pricePC2 = currency(_data.productData.pricing.PC2);
                    item.product.pricePC2PG2 = currency(_data.productData.pricing.PC2PG2);
                    item.product.priceP60D2 = currency(_data.productData.pricing.P60D2);
                    item.product.priceT1E2T1E6 = currency(_data.productData.pricing.T1E2T1E6);
                    item.product.priceT1E2 = currency(_data.productData.pricing.T1E2);
                    item.product.priceT1E3 = currency(_data.productData.pricing.T1E3);
                    item.product.priceT1E4 = currency(_data.productData.pricing.T1E4);
                    item.product.priceT1A4T1I2 = currency(_data.productData.pricing.T1A4T1I2);
                    item.product.priceT1A2T1I4 = currency(_data.productData.pricing.T1A2T1I4);
                    item.product.priceT1G2 = currency(_data.productData.pricing.T1G2);
                    item.product.priceT1F2 = currency(_data.productData.pricing.T1F2);
                    item.product.priceT2E2T2E6 = currency(_data.productData.pricing.T2E2T2E6);
                    item.product.priceT2E2 = currency(_data.productData.pricing.T2E2);
                    item.product.priceT2E3 = currency(_data.productData.pricing.T2E3);
                    item.product.priceT2E4 = currency(_data.productData.pricing.T2E4);
                    item.product.priceT2A4T2I2 = currency(_data.productData.pricing.T2A4T2I2);
                    item.product.priceT2A2T2I4 = currency(_data.productData.pricing.T2A2T2I4);
                    item.product.priceT2G2 = currency(_data.productData.pricing.T2G2);
                    item.product.priceT2F2 = currency(_data.productData.pricing.T2F2);
                    item.product.priceSCR1B = currency(_data.productData.pricing.SCR1B);
                    item.product.priceSCR1A = currency(_data.productData.pricing.SCR1A);
                    item.product.priceSCR1W = currency(_data.productData.pricing.SCR1W);
                    item.product.priceSCR1OM = currency(_data.productData.pricing.SCR1OM);
                    item.product.priceVTB = currency(_data.productData.pricing.VTB);
                    item.product.priceWCB = currency(_data.productData.pricing.WCB);
                    item.product.priceWOB = currency(_data.productData.pricing.WOB);
                    item.product.priceWWB = currency(_data.productData.pricing.WWB);
                    item.product.priceWGB = currency(_data.productData.pricing.WGB);
                    item.product.priceWBA = currency(_data.productData.pricing.WBA);
                    item.product.priceWBB = currency(_data.productData.pricing.WBB);
                    item.product.pricePEW = currency(_data.productData.pricing.PEW);
                    item.product.pricePCY = currency(_data.productData.pricing.PCY);
                    item.product.pricePDP = currency(_data.productData.pricing.PDP);
                    item.product.pricePFG = currency(_data.productData.pricing.PFG);
                    item.product.pricePOB = currency(_data.productData.pricing.POB);
                    item.product.pricePBO = currency(_data.productData.pricing.PBO);
                    item.product.pricePSG = currency(_data.productData.pricing.PSG);
                    item.product.pricePM1 = currency(_data.productData.pricing.PM1);
                    item.product.pricePM2 = currency(_data.productData.pricing.PM2);
                    item.product.pricePM3P = currency(_data.productData.pricing.PM3P);
                    item.product.pricePM3B = currency(_data.productData.pricing.PM3B);
                    item.product.pricePM3PB = currency(_data.productData.pricing.PM3PB);
                    item.product.priceL = currency(_data.productData.pricing.L);
                    item.product.priceS = currency(_data.productData.pricing.S);
                    item.product.priceFAS = currency(_data.productData.pricing.FAS);
                    item.product.priceFBU = currency(_data.productData.pricing.FBU);
                    item.product.priceFPW = currency(_data.productData.pricing.FPW);
                    item.product.priceFSA = currency(_data.productData.pricing.FSA);
                    item.product.priceFCH = currency(_data.productData.pricing.FCH);
                    item.product.priceFPE = currency(_data.productData.pricing.FPE);
                    item.product.priceFMU = currency(_data.productData.pricing.FMU);
                    item.product.priceFVA = currency(_data.productData.pricing.FVA);
                    item.product.priceFIR = currency(_data.productData.pricing.FIR);
                    item.product.priceFSB = currency(_data.productData.pricing.FSB);
                    item.product.priceFAQ = currency(_data.productData.pricing.FAQ);
                    item.product.priceFMB = currency(_data.productData.pricing.FMB);
                    item.product.priceFOL = currency(_data.productData.pricing.FOL);
                    item.product.priceFPO = currency(_data.productData.pricing.FPO);
                    item.product.priceYUS = currency(_data.productData.pricing.YUS);
                    item.product.priceKIS = currency(_data.productData.pricing.KIS);
                    item.product.priceHIS = currency(_data.productData.pricing.HIS);
                    item.product.priceBLS = currency(_data.productData.pricing.BLS);
                    item.product.priceSST = currency(_data.productData.pricing.SST);
                    item.product.priceCTT = currency(_data.productData.pricing.CTT);
                    item.product.priceRTT = currency(_data.productData.pricing.RTT);
                    item.product.priceWNB = currency(_data.productData.pricing.WNB);
                    item.product.priceCNC = currency(_data.productData.pricing.CNC);
                    item.product.priceCDK = currency(_data.productData.pricing.CDK);
                    item.product.priceLTF = currency(_data.productData.pricing.LTF);
                    item.product.priceLTP = currency(_data.productData.pricing.LTP);
                    item.product.priceLTW = currency(_data.productData.pricing.LTW);
                    item.product.priceLTA = currency(_data.productData.pricing.LTA);
                    item.product.priceLAM = currency(_data.productData.pricing.LAM);
                    item.product.priceWVA = currency(_data.productData.pricing.WVA);
                    item.product.priceWVW = currency(_data.productData.pricing.WVW);
                    item.product.priceWPW = currency(_data.productData.pricing.WPW);
                    item.product.priceLLK = currency(_data.productData.pricing.LLK);
                    item.product.priceWMP = currency(_data.productData.pricing.WMP);
                    item.product.priceBCR = currency(_data.productData.pricing.BCR);
                    item.product.priceASE = currency(_data.productData.pricing.ASE);
                    item.product.priceEGB = currency(_data.productData.pricing.EGB);
                    item.product.priceWIN = currency(_data.productData.pricing.WIN);
                    item.product.priceWCH = currency(_data.productData.pricing.WCH);
                    item.product.priceFLW = currency(_data.productData.pricing.FLW);
                    item.product.priceBFC = currency(_data.productData.pricing.BFC);
                    item.product.priceBLA = currency(_data.productData.pricing.BLA);
                    item.product.pricePBB = currency(_data.productData.pricing.PBB);
                    item.product.pricePBX = currency(_data.productData.pricing.PBX);
                    item.product.pricePTS = currency(_data.productData.pricing.PTS);
                    item.product.pricePTL = currency(_data.productData.pricing.PTL);
                    item.product.pricePHR = currency(_data.productData.pricing.PHR);
                    item.product.priceRCW = currency(_data.productData.pricing.RCW);
                    item.product.priceRBD = currency(_data.productData.pricing.RBD);
                    item.product.priceMBT = currency(_data.productData.pricing.MBT);
                    item.product.unitListPrice = _data.unitListPrice(item.product) + _data.unitTextilePrice(item.product);
                    item.product.unitTextilePrice = _data.unitTextilePrice(item.product);
                    item.product.unitNetPrice = _data.unitNetPrice(item.product);
                    item.product.unitTaxPrice = _data.unitTaxPrice(item.product);
                  }
                  // Increase count
                  count++;
                  // If we've updated all products,
                  // set data and save the quote
                  if (count >= _data.workingData.products.length) {
                    // Ensure approval state is empty
                    _data.workingData.approvalState = null;
                    // Get current date
                    var dateUpdated = new Date();
                    var validUntil = new Date();
                    // Add 30 days to current date
                    dateUpdated.setDate(dateUpdated.getDate());
                    validUntil.setDate(validUntil.getDate() + 30);
                    // Update valid until date to be 30 days out from save
                    _data.workingData.dateQuoteUpdated = dateUpdated.toISOString();
                    _data.workingData.validUntilDate = validUntil.toISOString();

                    // Load surcharge pricing
                    axios.get(`/surcharges.json`)
                      .then(function (response) {
                        // Set minimum order surcharge data
                        _data.surchargesData = response.data;

                        _data.workingData.minimumOrderSurcharge = _data.surchargesData.minimumOrderSurcharge;
                        _data.workingData.minimumOrderThreshold = _data.surchargesData.minimumOrderThreshold;
                        if (existingFreightSurcharge === null || existingFreightSurcharge === '' || typeof existingFreightSurcharge === 'undefined') {
                          _data.workingData.freightSurcharge = _data.surchargesData.freightSurcharge;
                        } else {
                          _data.workingData.freightSurcharge = existingFreightSurcharge;
                        }

                        if ((_data.workingData.totalNetPrice < _data.workingData.minimumOrderThreshold )&& _data.workingData.waiveMinimumOrderSurcharge !== 'yes') {
                            _data.workingData.totalQuotePrice = (_data.quoteTotalPrice + _data.workingData.minimumOrderSurcharge);
                        }
                    
                        // Load upcharge pricing
                        axios.get(`/upcharges.json`)
                          .then(function (response) {
                            // Set minimum order upcharge data
                            _data.upchargesData = response.data;
                            
                            if (existingLiftGateUpcharge === null || existingLiftGateUpcharge === '' || typeof existingLiftGateUpcharge === 'undefined') {
                              _data.workingData.liftGateUpcharge = _data.upchargesData.liftGateUpcharge;
                            } else {
                              _data.workingData.liftGateUpcharge = existingLiftGateUpcharge;
                            }
                            if (existingResidentialDeliveryUpcharge === null || existingResidentialDeliveryUpcharge === '' || typeof existingResidentialDeliveryUpcharge === 'undefined') {
                              _data.workingData.residentialDeliveryUpcharge = _data.upchargesData.residentialDeliveryUpcharge;
                            } else {
                              _data.workingData.residentialDeliveryUpcharge = existingResidentialDeliveryUpcharge;
                            }
                            if (existingDeliveryAppointmentUpcharge === null || existingDeliveryAppointmentUpcharge === '' || typeof existingDeliveryAppointmentUpcharge === 'undefined') {
                              _data.workingData.deliveryAppointmentUpcharge = _data.upchargesData.deliveryAppointmentUpcharge;
                            } else {
                              _data.workingData.deliveryAppointmentUpcharge = existingDeliveryAppointmentUpcharge;
                            }

                            // Save quote
                            _data.saveQuote();
                          });
                      });
                  }
              });
            });
          })
          .catch(function (error) {
            console.log(error);
        });
      } else {
        // Ensure approval state is empty
        this.workingData.approvalState = null;
        // Get current date
        var dateUpdated = new Date();
        var validUntil = new Date();
        // Add 30 days to current date
        dateUpdated.setDate(dateUpdated.getDate());
        validUntil.setDate(validUntil.getDate() + 30);
        // Update valid until date to be 30 days out from save
        this.workingData.dateQuoteUpdated = dateUpdated.toISOString();
        this.workingData.validUntilDate = validUntil.toISOString();
        // Save quote
        this.saveQuote();
      }
    },
    adminApproveQuote() {
      // Set type as quote
      this.workingData.quoteType = 'quote';
      // Update approval state
      this.workingData.approvalState = 'adminApproved';
      // Save quote with change
      this.saveQuote();
    },
    adminDenyQuote() {
      // Set type as estimate
      this.workingData.quoteType = 'estimate';
      // Update approval state
      this.workingData.approvalState = 'adminDenied';
      // Save quote with change
      this.saveQuote();
    },
    buildModuleYardage(_target) {
      // Establish variable to
      // hold yardage totals
      var totalYardage = 0;

      // If module one exists
      if (_target.moduleOne.length) {
        // If TAN, SET, or none
        if (_target.moduleOne[0].code == 'TAN') {
          totalYardage += _target.yardageTAN;
        } else if (_target.moduleOne[0].code == 'SET') {
          totalYardage += _target.yardageSET;
        } else {
          totalYardage += 0;
        }
      }

      // If module three exists
      if (_target.moduleThree.length) {
          // If TAN, SET, or none
        if (_target.moduleThree[0].code == 'TAN' && _target.moduleOne[0].code != 'SET') {
          totalYardage += _target.yardageTAN;
        } else if (_target.moduleThree[0].code == 'SET' && _target.moduleOne[0].code != 'SET') {
          totalYardage += _target.yardageSET;
        } else {
          totalYardage += 0;
        }
      }

      // If module five exists
      if (_target.moduleFive.length) {
        // If TAN or none
        if (_target.moduleFive[0].code == 'TAN' && _target.moduleThree[0].code != 'SET') {
          totalYardage += _target.yardageTAN;
        } else {
          totalYardage += 0;
        }
      }

      // Push totals back to UI
      return parseFloat(totalYardage);
    },
    onLeave() {
      if (!this.saveDisabled) {
        return true;
      } else {
        return false;
      }
    },
    fetchLineArtUrl(x) {
      const isCandyProduct = candyProductBaseCodes.some(baseCode => x.baseCode.indexOf(baseCode) === 0);
      let imageUrl = '';
      let filename = `
        ${x.baseCode}
        ${(isCandyProduct && (x.tableTopSize.length > 0) && x.tableTopSize[0].code) ? `-${x.tableTopSize[0].code}` : ''}
        ${((x.plinthType.length > 0) && x.plinthType[0].code) ? `-${x.plinthType[0].code}` : ''}
        ${((x.species.length > 0) && x.species[0].code && !this.isMaru(x.baseCode)) ? `-${x.species[0].code}` : ''}
        ${(x.moduleOne.length > 0) ? ((x.moduleOne[0].code) ? `-${x.moduleOne[0].code}` : '') : '' }
        ${(x.moduleOne.length > 0) ? (x.baseCode === 'GC01-30' && x.moduleOne[0].code === 'SET' ? `-NA` : ((x.moduleTwo.length > 0) ? (x.moduleTwo[0].code ? `-${x.moduleTwo[0].code}` : '') : '')) : '' }
        ${(x.moduleOne.length > 0) ? (x.baseCode === 'GC01-30' && x.moduleOne[0].code === 'SET' ? `-NA` : ((x.moduleThree.length > 0) ? (x.moduleThree[0].code ? `-${x.moduleThree[0].code}` : '') : '')) : '' }
        ${(x.moduleThree.length > 0) ? (x.baseCode === 'GC01-30' && x.moduleThree[0].code === 'SET' ? `-NA` : ((x.moduleFour.length > 0) ? (x.moduleFour[0].code ? `-${x.moduleFour[0].code}` : '') : '')) : '' }
        ${(x.moduleThree.length > 0) ? (x.baseCode === 'GC01-30' && x.moduleThree[0].code === 'SET' ? `-NA` : ((x.moduleFive.length > 0) ? (x.moduleFive[0].code ? `-${x.moduleFive[0].code}` : '') : '')) : '' }
        ${((x.armHeight.length > 0) && x.armHeight[0].code) ? `-${x.armHeight[0].code}` : ''}
        ${(!isCandyProduct && (x.tableTopSize.length > 0) && x.tableTopSize[0].code) ? `-${x.tableTopSize[0].code}` : ''}
        ${((x.pads.length > 0) && x.pads[0].code !== 'XX') ? `-${x.pads[0].code}` : ''}
        ${((x.tables.length > 0) && x.tables[0].code !== 'XX') ? `-${x.tables[0].code}` : ''}
        ${((x.partitions.length > 0) && x.partitions[0].code !== 'XX') ? `-${x.partitions[0].code}` : ''}
      `;
      filename = filename.replace(/^\s+|\s+$/gm, '').split('\n').join('')
      imageUrl = `/uploads/line-art/${filename}.svg`;
      return imageUrl;
    },
    unitListPrice(_node) {
      let price = currency(_node.baseCom)
        .add((_node.species.length && _node.species[0].code) ? _node[`price${_node.species[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.moduleOne.length && _node.moduleOne[0].code) ? _node[`price${_node.moduleOne[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.moduleTwo.length && _node.moduleTwo[0].code) && !(this.isThreeSeat === 1 && _node.moduleOne[0].code === 'SET') ? _node[`price${_node.moduleTwo[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.moduleThree.length && _node.moduleThree[0].code) && !(this.isThreeSeat === 1 && _node.moduleOne[0].code === 'SET') ? _node[`price${_node.moduleThree[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.moduleFour.length && _node.moduleFour[0].code) && !(this.isThreeSeat === 1 && _node.moduleThree[0].code === 'SET') ? _node[`price${_node.moduleFour[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.moduleFive.length && _node.moduleFive[0].code) && !(this.isThreeSeat === 1 && _node.moduleThree[0].code === 'SET') ? _node[`price${_node.moduleFive[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.armHeight.length && _node.armHeight[0].code) ? _node[`price${_node.armHeight[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.armCapMaterial.length && _node.armCapMaterial[0].code) ? _node[`price${_node.armCapMaterial[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.pads.length && _node.pads[0].code) ? _node[`price${_node.pads[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.plinthType.length && _node.plinthType[0].code) ? _node[`price${_node.plinthType[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.plinthMaterials.length && _node.plinthMaterials[0].code) ? _node[`price${_node.plinthMaterials[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.plinthMaterials.length && _node.plinthMaterials[0].code === 'LAM' && _node.plinthLaminateBrands.length && _node.plinthLaminateBrands[0].code) ? _node[`price${_node.plinthLaminateBrands[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.plinthFinishes.length && _node.plinthFinishes[0].code) ? _node[`price${_node.plinthFinishes[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.tables.length && _node.tables[0].code) ? _node[`price${_node.tables[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.partitions.length && _node.partitions[0].code) ? _node[`price${_node.partitions[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.powerModules.length && _node.powerModules[0].code) ? _node[`price${_node.powerModules[0].code.replace(/-/g, '')}`] : 0)
        .add((((_node.tableTopMaterial.length && _node.tableTopMaterial[0].code) && (((_node.moduleThree.length || _node.moduleFive.length) && (_node.moduleOne[0].code === 'INL' || _node.moduleThree[0].code === 'INL' || (_node.moduleFive.length && _node.moduleFive[0].code === 'INL')))) && !this.isMaru(_node.baseCode)) || (this.isMaru(_node.baseCode) && ((_node.tableTopMaterial.length && !_node.tables.length) || (_node.tableTopMaterial.length && _node.tables.length && _node.tables[0].code !== 'XX'))) || ((_node.tableTopMaterial.length && _node.tableTopMaterial[0].code) && !this.isMaru(_node.baseCode) && !_node.moduleOne.length && !_node.moduleTwo.length && !_node.moduleThree.length && !_node.moduleFour.length && !_node.moduleFive.length)) ? _node[`price${_node.tableTopMaterial[0].code.replace(/-/g, '')}`] : 0) // Requires "INL" be set on at least one module, if modules exist
        .add((_node.tableTopMaterialFinishSideA.length && _node.tableTopMaterialFinishSideA[0].code) ? _node[`price${_node.tableTopMaterialFinishSideA[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.tableTopMaterial.length && (_node.tableTopMaterial[0].code === 'FTT' || _node.tableTopMaterial[0].code === 'LTT')) && (_node.tableTopMaterialFinishSideB.length && _node.tableTopMaterialFinishSideB[0].code)) ? _node[`price${_node.tableTopMaterialFinishSideB[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.tableTopMaterial.length && _node.tableTopMaterial[0].code === 'FTT') && (_node.tableTopMaterialForboFinish.length && _node.tableTopMaterialForboFinish[0].code)) ? _node[`price${_node.tableTopMaterialForboFinish[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.tableTopMaterial.length && _node.tableTopMaterial[0].code === 'MTT') && (_node.tableTopMaterialMetalFinish.length && _node.tableTopMaterialMetalFinish[0].code)) ? _node[`price${_node.tableTopMaterialMetalFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.largeInsertTopMaterial.length && _node.largeInsertTopMaterial[0].code) ? _node[`price${_node.largeInsertTopMaterial[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.largeInsertTopMaterial.length && _node.largeInsertTopMaterial[0].code === 'FTL') && (_node.largeInsertTopMaterialForboFinish.length && _node.largeInsertTopMaterialForboFinish[0].code)) ? _node[`price${_node.largeInsertTopMaterialForboFinish[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.largeInsertTopMaterial.length && _node.largeInsertTopMaterial[0].code === 'SSL') && (_node.largeInsertTopMaterialSolidSurfaceFinish.length && _node.largeInsertTopMaterialSolidSurfaceFinish[0].code)) ? _node[`price${_node.largeInsertTopMaterialSolidSurfaceFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.smallInsertTopMaterial.length && _node.smallInsertTopMaterial[0].code) ? _node[`price${_node.smallInsertTopMaterial[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.smallInsertTopMaterial.length && _node.smallInsertTopMaterial[0].code === 'FTS') && (_node.smallInsertTopMaterialForboFinish.length && _node.smallInsertTopMaterialForboFinish[0].code)) ? _node[`price${_node.smallInsertTopMaterialForboFinish[0].code.replace(/-/g, '')}`] : 0)
        .add(((_node.smallInsertTopMaterial.length && _node.smallInsertTopMaterial[0].code === 'SSS') && (_node.smallInsertTopMaterialSolidSurfaceFinish.length && _node.smallInsertTopMaterialSolidSurfaceFinish[0].code)) ? _node[`price${_node.smallInsertTopMaterialSolidSurfaceFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.caseworkSurfaceMaterial.length && _node.caseworkSurfaceMaterial[0].code) ? _node[`price${_node.caseworkSurfaceMaterial[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.columnShroudPaint.length && _node.columnShroudPaint[0].code) ? _node[`price${_node.columnShroudPaint[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.legMaterial.length && _node.legMaterial[0].code) ? _node[`price${_node.legMaterial[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.metalFinish.length && _node.metalFinish[0].code) && (_node.legMaterial.length && _node.legMaterial[0].code !== 'LW') ? _node[`price${_node.metalFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.woodFinish.length && _node.woodFinish[0].code) && (!_node.legMaterial.length || (_node.legMaterial.length && _node.legMaterial[0].code !== 'LM')) && !(_node.baseCode == 'MM01-T1' || _node.baseCode == 'MM01-T2' || _node.baseCode == 'MM01-T3') && !this.isProse(_node.baseCode) ? _node[`price${_node.woodFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.woodFinish.length && _node.woodFinish[0].code) && ((_node.baseCode == 'MM01-T1' || _node.baseCode == 'MM01-T2' || _node.baseCode == 'MM01-T3' || _node.baseCode == 'NC01-T1') && ((_node.largeInsertTopMaterial.length && (_node.largeInsertTopMaterial[0].code == 'VAL' || _node.largeInsertTopMaterial[0].code == 'VWL')) || (_node.smallInsertTopMaterial.length && (_node.smallInsertTopMaterial[0].code == 'VAS' || _node.smallInsertTopMaterial[0].code == 'VWS' || _node.smallInsertTopMaterial[0].code == 'SAS' || _node.smallInsertTopMaterial[0].code == 'SWS')))) && !this.isProse(_node.baseCode) ? _node[`price${_node.woodFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.woodFinish.length && _node.woodFinish[0].code) && this.isProse(_node.baseCode) && (this.finishType !== 'paintedWood') ? _node[`price${_node.woodFinish[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.paintedWood.length && _node.paintedWood[0].code && (this.finishType !== 'woodFinish')) ? _node[`price${_node.paintedWood[0].code}1`] : 0)
        .add((_node.stoneQuartzTableTop.length && _node.stoneQuartzTableTop[0].code) && (_node.smallInsertTopMaterial.length && _node.smallInsertTopMaterial[0].code == 'STS') ? _node[`price${_node.stoneQuartzTableTop[0].code.replace(/-/g, '')}`] : 0)
        .add(_node.moistureBarrier ? _node.priceMB : 0)
        .add(_node.removeableCovers ? _node.priceRC : 0)
        .add(_node.calTb133 ? _node.priceFB : 0)
        .add(_node.casters ? _node.priceCAS : 0)
        .add(_node.removeableArmPads ? _node.priceAP : 0)
        .add(_node.hangingFixture ? _node.priceDJ: 0)
        .add((_node.hingesOne.length && _node.hingesOne[0].code) ? _node[`price${_node.hingesOne[0].code}1`] : 0)
        .add((_node.hingesTwo.length && _node.hingesTwo[0].code) ? _node[`price${_node.hingesTwo[0].code}2`] : 0)
        .add((_node.hingesThree.length && _node.hingesThree[0].code) ? _node[`price${_node.hingesThree[0].code}3`] : 0)
        .add((_node.pull.length && _node.pull[0].code) ? _node[`price${_node.pull[0].code.replace(/-/g, '')}`] : 0)
        .add((_node.wrap.length && _node.wrap[0].code) ? _node[`price${_node.wrap[0].code.replace(/-/g, '')}`] : 0)
        .add(_node.drawerLiner ? _node.priceDL : 0)
        .add((_node.veneerOuterCase.length && _node.veneerOuterCase[0].code) ? _node[`price${_node.veneerOuterCase[0].code.replace(/-/g, '')}`] : 0)
        .add(_node.paperTowelDispenser ? _node.priceTD : 0)
        .add(_node.extraAdjustableShelf ? _node.priceAS : 0)
        .add(_node.fileHolders ? _node.priceFH : 0)
        .add(_node.gloveCutOuts ? _node.priceGH : 0)
        .add((_node.multTextilesOption.length && _node.multTextilesOption[0].code) ? _node[`price${_node.multTextilesOption[0].code.replace(/-/g, '')}`] : 0);

      // Round cents up to nearest dollar
      price.value = Math.ceil(price.value);

      return price.value;
    },
    unitTextilePrice(_node) {
      // Reset values
      this.singleTextilePrice = 0;
      this.armTextilePrice = 0;
      this.backTextilePrice = 0;
      this.seatTextilePrice = 0;
      this.sideATextilePrice = 0;
      this.sideBTextilePrice = 0;
      this.moduleYardage = 0;

      // If single textile
      if ((_node.yardageSeat > 0 || _node.yardageBack > 0 || _node.yardageArm > 0 || _node.yardageTop > 0 || _node.yardageTAN > 0 || _node.yardageSET > 0 || _node.yardageSideA > 0 || _node.yardageSideB > 0 || _node.pads.length)) {
        // If a single has module textiles
        if ((_node.moduleOne.length || _node.moduleTwo.length || _node.moduleThree.length || _node.moduleFour.length || _node.moduleFive.length) && _node.singleTextileGrade.length) {
          if (_node.moduleThree.length && !_node.moduleFive.length) {
            this.moduleYardage += _node.moduleOne[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
            this.moduleYardage += _node.moduleThree[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
          } else if (_node.moduleFive.length) {
            if (_node.moduleOne[0].code === 'SET') {
              this.moduleYardage += _node.moduleOne[0].code === 'SET' ? parseFloat(_node.yardageSET) : 0;
              this.moduleYardage += _node.moduleFive[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
            } else if (_node.moduleThree[0].code === 'SET') {
              this.moduleYardage += _node.moduleOne[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
              this.moduleYardage += _node.moduleThree[0].code === 'SET' ? parseFloat(_node.yardageSET) : 0;
            } else {
              this.moduleYardage += _node.moduleOne[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
              this.moduleYardage += _node.moduleThree[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
              this.moduleYardage += _node.moduleFive[0].code === 'TAN' ? parseFloat(_node.yardageTAN) : 0;
            }
          }
          this.singleTextilePrice = (this.moduleYardage * _node[`price${_node.singleTextileGrade[0].code.replace(/-/g, '')}`]);
        } else {

          if (_node.baseCode == 'KN01-S1') {
            this.singleTextilePrice = (_node.yardageTotal > 0 && _node.tableTopMaterial[0].code === 'UPH') ? (parseFloat(_node.yardageTotal) * this.textileData[`price${_node.singleTextileGrade[0].code.replace(/-/g, '')}`]) : 0;
          } else {
            if (_node.singleTextileGrade.length > 0) {
              this.singleTextilePrice = ((this.isMaru(_node.baseCode) || this.isApril(_node.baseCode)) && _node.pads.length && !_node.multTextilesOption.length) ? (parseFloat(_node.pads[0].padYardage) * this.textileData[`price${_node.singleTextileGrade[0].code.replace(/-/g, '')}`]) : (_node.yardageTotal * this.textileData[`price${_node.singleTextileGrade[0].code.replace(/-/g, '')}`]);
            } else {
              this.singleTextilePrice = 0;
            }
          }
        }
      }

      // If multiple textile(s)
      if (_node.multTextilesOption.length) {
        // If not set to XX
        if (_node.multTextilesOption[0].code !== 'XX') {
          this.singleTextilePrice = 0;
        }
        // If set to TA
        if (_node.multTextilesOption[0].code === 'TA') {
          // Set seat
          this.seatTextilePrice = (this.isMaru(_node.baseCode) && _node.pads.length) ? (_node.pads[0].padYardage * this.textileData[`price${_node.seatTextileGrade[0].code.replace(/-/g, '')}`]) : (_node.yardageSeat * this.textileData[`price${_node.seatTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set back
          this.backTextilePrice =
            (_node.yardageBack * _node[`price${_node.backTextileGrade[0].code.replace(/-/g, '')}`]);
        }
        // If set to AT
        if (_node.multTextilesOption[0].code === 'AT') {
          // Set arm
          this.armTextilePrice =
            (_node.yardageArm * _node[`price${_node.armTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set seat
          this.seatTextilePrice =
            (_node.yardageSeat * _node[`price${_node.seatBackTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set back
          this.backTextilePrice =
            (_node.yardageBack * _node[`price${_node.seatBackTextileGrade[0].code.replace(/-/g, '')}`]);
        }
        // If set to ST
        if (_node.multTextilesOption[0].code === 'ST') {
          // Set seat
          this.seatTextilePrice =
            (_node.yardageSeat * _node[`price${_node.seatTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set arm
          this.armTextilePrice =
            (_node.yardageArm * _node[`price${_node.armBackTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set back
          this.backTextilePrice =
            (_node.yardageBack * _node[`price${_node.armBackTextileGrade[0].code.replace(/-/g, '')}`]);
        }
        // If set to BT
        if (_node.multTextilesOption[0].code === 'BT') {
          // Set back
          this.backTextilePrice =
            (_node.yardageBack * _node[`price${_node.backTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set arm
          this.armTextilePrice =
            (_node.yardageArm * _node[`price${_node.armSeatTextileGrade[0].code.replace(/-/g, '')}`]);
          // Set seat
          this.seatTextilePrice =
            (_node.yardageSeat * _node[`price${_node.armSeatTextileGrade[0].code.replace(/-/g, '')}`]);
        }
        // If set to AB
        if (_node.multTextilesOption[0].code === 'AB') {
          // Set side a
          this.sideATextilePrice =
            (_node.yardageSideA * _node[`price${_node.sideATextileGrade[0].code.replace(/-/g, '')}`]);
          // Set side b
          this.sideBTextilePrice =
            (_node.yardageSideB * _node[`price${_node.sideBTextileGrade[0].code.replace(/-/g, '')}`]);
        }
      }

      // Add up totals
      let price = currency(this.singleTextilePrice ? this.singleTextilePrice : 0).add(this.armTextilePrice ? this.armTextilePrice : 0).add(this.backTextilePrice ? this.backTextilePrice : 0).add(this.seatTextilePrice ? this.seatTextilePrice : 0).add(this.sideATextilePrice ? this.sideATextilePrice : 0).add(this.sideBTextilePrice ? this.sideBTextilePrice : 0);

      price.value = Math.ceil(price.value);
      return price.value;
    },
    unitNetPrice(_node) {
        let listTextile = this.unitListPrice + this.unitTextilePrice;
        let price = currency(listTextile - (listTextile * _node.productDiscount / 100));

        return price.value;
    },
    unitTaxPrice(_node) {
        let price = currency((this.unitNetPrice * this.workingData.salesTax) / 100);

        return price.value;
    }
  },
  computed: {
      totalUnits: function() {
          return this.workingData.products.reduce((total, item, n) => {
            let value = currency(total).add(item.product.quantity);
            let valueCleaned =  parseFloat(value);
            return parseFloat(valueCleaned);
          }, 0);
      },
      unitNetRowPrice: function() {
        return this.workingData.products.map((item) => {
          let listTextile = item.product.unitListPrice;
          let price = currency(listTextile - (listTextile * item.product.productDiscount / 100));
          let valueCleaned =  parseFloat(price).toFixed(2).replace(/\.00/g,'');

          return parseFloat(valueCleaned);
        });
      },
      unitTaxRowPrice: function() {
        return this.workingData.products.map((item) => {
          let value = currency((item.product.unitNetPrice * this.workingData.salesTax) / 100);
          let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
          return parseFloat(valueCleaned);
        });
      },
      extendedListRowPrice: function() {
        return this.workingData.products.map((item) => {
          let value = currency(item.product.quantity * item.product.unitListPrice);
          let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
          return parseFloat(valueCleaned);
        });
      },
      extendedTextileRowPrice: function() {
        return this.workingData.products.map((item) => {
          let value = currency(item.product.quantity * item.product.unitTextilePrice);
          let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
          return parseFloat(valueCleaned);
        });
      },
      extendedNetRowPrice: function() {
        return this.workingData.products.map((item) => {
          let value = currency(item.product.quantity * item.product.unitNetPrice);
          let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
          return parseFloat(valueCleaned);
        });
      },
      extendedTaxRowPrice: function() {
        return this.workingData.products.map((item) => {
          let value = currency(item.product.quantity * item.product.unitTaxPrice);
          let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
          return parseFloat(valueCleaned);
        });
      },
      quoteListPrice: function() {
          return this.workingData.products.reduce((total, item, n) => {
            let value = currency(total).add(item.product.extendedListPrice);
            let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
            return parseFloat(valueCleaned);
          }, 0);
      },
      quoteTextilePrice: function() {
          return this.workingData.products.reduce((total, item, n) => {
            let value = currency(total).add(item.product.extendedTextilePrice);
            let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
            return parseFloat(valueCleaned);
          }, 0);
      },
      quoteNetPrice: function() {
          return this.workingData.products.reduce((total, item, n) => {
            let value = currency(total).add(item.product.extendedNetPrice);
            let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
            return parseFloat(valueCleaned);
          }, 0);
      },
      quoteFreightSurchargeTotalPrice: function() {
          let liftGateTotal = this.workingData.liftGate >= 1 ? this.workingData.liftGateUpcharge : 0;
          let residentialDeliveryTotal = this.workingData.residentialDelivery >= 1 ? this.workingData.residentialDeliveryUpcharge : 0;
          let deliveryAppointmentTotal = this.workingData.deliveryAppointment >= 1 ? this.workingData.deliveryAppointmentUpcharge : 0;
          let value = currency(((this.workingData.totalListPrice * this.workingData.freightSurcharge) / 100) + liftGateTotal + residentialDeliveryTotal + deliveryAppointmentTotal);
          let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
          return parseFloat(valueCleaned);
      },
      // quoteTaxPrice: function() {
      //     return this.workingData.products.reduce((total, item, n) => {
      //       let value = currency(total).add(item.product.extendedTaxPrice);
      //       let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
      //       return parseFloat(valueCleaned);
      //     }, 0);
      // },
      quoteTotalPrice: function() {
          return this.workingData.products.reduce((total, item, n) => {
            let value = currency(total).add(item.product.extendedNetPrice).add(item.product.extendedTaxPrice);
            let valueCleaned =  parseFloat(value).toFixed(2).replace(/\.00/g,'');
            return parseFloat(valueCleaned);
          }, 0);
      },
      setQuoteID() {
        var y = parse(this.workingData.dateCreated.date).toLocaleString('en-US', { year: 'numeric' });
        var m = parse(this.workingData.dateCreated.date).toLocaleString('en-US', { month: 'numeric' }).padStart(2,0);
        var d = parse(this.workingData.dateCreated.date).toLocaleString('en-US', { day: 'numeric' }).padStart(2,0);
        let quoteID = this.quoteId;

        return quoteID;
      },
      hasCustomProducts() {
        // Establish our return holder
        var returnVal = false;
        // Loop all products and if a custom product is found,
        // set our return value as "true" and trigger UI components
        for (var i=0; i < this.workingData.products.length; i++) {
          // If this product is a custom product, set our return value to true
          if (this.workingData.products[i].product.productSlug == 'custom-product') {
            returnVal = true;
          }
        }
        // Return the result
        return returnVal;
      }
  },
  filters: {
    formatPrice(x) {
      if (!x) return;
      x = x.toString();
      x = Math.ceil(x);
      return currency(x).format({symbol: true});
    },
    formatNetPrice(x) {
      if (!x) return;
      x = x.toString();
      return currency(x).format({symbol: true});
    },
    formatDate(x) {
      var t = parse(x).toLocaleString(
        'en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      return t;
    }
  },
  watch: {
      workingData: {
          handler() {
              // Set state of save disabled based on whether a change has been made
              this.saveDisabled = _.isEqual(this.workingData, this.savedData);
          },
          deep: true
      }
  },
  updated: function () {
    this.$nextTick(function () {
      // Check for returning hash
      // and scroll if it exists
      if(window.location.hash) {
        var element = document.getElementById('product__'+window.location.hash.substring(1));
        var headerOffset = 50;
        var elementPosition = element.getBoundingClientRect().top;
        var offsetPosition = elementPosition - headerOffset;

        window.scrollTo({
             top: offsetPosition,
             behavior: "smooth"
        });

        // Remove hash
        var noHashURL = window.location.href.replace(/#.*$/, '');
        window.history.replaceState('', document.title, noHashURL);
      }
    });
  },
  created: function () {
    // Check whether or not there is unsaved data
    window.onbeforeunload = () => {
      // Get saved results
      var result = this.onLeave();

      // If unsaved results exist,
      // display dialog box to user
      if (result) {
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    }
  }
}
</script>
